import React, { useState, useRef, useEffect, useCallback, useMemo, useContext, createContext } from "react";
import "./App.css";
import { marked } from "marked";
import JSZip from "jszip";
//import { saveAs } from "file-saver";
import { v4 as uuidv4 } from "uuid";
//import Select from 'react-select';
// eslint-disable-next-line
import { FaBolt, FaBalanceScale, FaBrain, FaUserCircle, FaBook, FaSearch, FaLink, FaTools, FaBullseye, FaPlus, FaCog, FaUser, FaSignOutAlt, FaFile, FaHistory, FaRobot, FaComments } from 'react-icons/fa';
import './CommandPalette.css';
import * as Sentry from "@sentry/react";
import { toast, ToastContainer, Slide } from 'react-toastify';

Sentry.init({
  dsn: "https://04db50794407adfc666e7e2d755816e5@o4504010979213312.ingest.us.sentry.io/4509883567112192",
  sendDefaultPii: true,
  integrations: [
    // Sentry.feedbackIntegration({
    //   colorScheme: "system",
    //   position: "top-right",
    //   isNameRequired: true,
    //   isEmailRequired: true,
    //   button: false, // hides default Sentry button
    // }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// eslint-disable-next-line
const options = [
  { value: 'fast', label: <><FaBolt /> Fast</> },
  { value: 'balanced', label: <><FaBalanceScale /> Balanced</> },
  { value: 'deep', label: <><FaBrain /> Deep Analysis</> },
];

const ToastContext = createContext();

const ToastProvider = ({ children }) => {
  const [activeToasts, setActiveToasts] = useState([]);

  const addToast = ({
    message,
    type = 'default',
    duration = 5000,
    position = 'top-right',
    hideProgressBar = false,
    closeOnClick = true,
    pauseOnHover = true,
    draggable = true,
    theme = ['light', 'dark'].includes('') ? '' : localStorage.getItem('theme') || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') || 'dark',
    icon,
    onClick,
    onClose,
    link,
    autoClose = true,
    closeButton = true,
    newestOnTop = true,
    rtl = false,
    transition = Slide,
    className,
    bodyClassName,
    progressClassName,
    toastId,
    mergeDuplicates = true // New option to control duplicate merging
  }) => {
    // Check for duplicates if mergeDuplicates is enabled
    if (mergeDuplicates) {
      const isDuplicate = activeToasts.some(
        toast => toast.message === message && toast.type === type
      );
      
      if (isDuplicate) {
        return; // Don't show duplicate toast
      }
    }

    const toastOptions = {
      position,
      autoClose: autoClose ? duration : false,
      hideProgressBar,
      closeOnClick,
      pauseOnHover,
      draggable,
      theme,
      transition,
      className,
      bodyClassName,
      progressClassName,
      toastId,
      closeButton,
      icon: icon || undefined,
      onClick: (event) => {
        if (onClick) onClick(event);
        if (link) {
          event.preventDefault();
          if (link.startsWith('http')) {
            window.open(link, '_blank');
          } else {
            window.location.href = link;
          }
        }
      },
      onClose: () => {
        // Remove toast from active toasts when closed
        setActiveToasts(prev => prev.filter(t => t.toastId !== toastId));
        if (onClose) onClose();
      }
    };

    // Enhanced message content for links
    const messageContent = link ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {message}
        <span style={{ fontSize: '12px', opacity: 0.8 }}>↗</span>
      </div>
    ) : message;

    // Map your custom types to react-toastify types
    const toastTypeMap = {
      success: () => {
        const id = toast.success(messageContent, toastOptions);
        setActiveToasts(prev => [...prev, { toastId: id, message, type }]);
        return id;
      },
      error: () => {
        const id = toast.error(messageContent, toastOptions);
        setActiveToasts(prev => [...prev, { toastId: id, message, type }]);
        return id;
      },
      warning: () => {
        const id = toast.warn(messageContent, toastOptions);
        setActiveToasts(prev => [...prev, { toastId: id, message, type }]);
        return id;
      },
      info: () => {
        const id = toast.info(messageContent, toastOptions);
        setActiveToasts(prev => [...prev, { toastId: id, message, type }]);
        return id;
      },
      default: () => {
        const id = toast(messageContent, toastOptions);
        setActiveToasts(prev => [...prev, { toastId: id, message, type }]);
        return id;
      },
      loading: () => {
        const id = toast.loading(messageContent, toastOptions);
        setActiveToasts(prev => [...prev, { toastId: id, message, type }]);
        return id;
      }
    };

    const toastFunction = toastTypeMap[type] || toastTypeMap.default;
    return toastFunction();
  };

  const removeToast = (toastId) => {
    toast.dismiss(toastId);
    setActiveToasts(prev => prev.filter(t => t.toastId !== toastId));
  };

  const removeAllToasts = () => {
    toast.dismiss();
    setActiveToasts([]);
  };

  const updateToast = (toastId, options) => {
    toast.update(toastId, options);
  };

  return (
    <ToastContext.Provider value={{ 
      addToast, 
      removeToast, 
      removeAllToasts, 
      updateToast 
    }}>
      {children}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
    </ToastContext.Provider>
  );
};

const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export { ToastProvider, useToast };

function AppWrapper() {
  return (
    <ToastProvider>
      <App />
    </ToastProvider>
  );
}

export function useCustomKeyHandlers({
  onSlashPress,
  onCtrlN,
  onCtrlR,
  onCtrlSpace,
  onCtrlOnly,
  onCtrlK
}) {
  useEffect(() => {
    let ctrlTimer = null;
    let ctrlPressedAlone = false;

    const handleKeyDown = (event) => {
      const isCtrl = event.ctrlKey || event.metaKey;

      if (event.key === '/' && !isCtrl) {
        event.preventDefault();
        event.stopPropagation();
        onSlashPress?.();
      }

      if (isCtrl && event.code === 'Space') {
        event.preventDefault();
        event.stopPropagation();
        clearTimeout(ctrlTimer);
        ctrlPressedAlone = false;
        onCtrlN?.();
      }

      if (isCtrl && event.code === 'Space') {
        event.preventDefault();
        event.stopPropagation();
        clearTimeout(ctrlTimer);
        ctrlPressedAlone = false;
        onCtrlSpace?.();
      }

      if (event.key === 'Control' || event.key === 'Meta') {
        ctrlPressedAlone = true;
        ctrlTimer = setTimeout(() => {
          if (ctrlPressedAlone) {
            onCtrlOnly?.();
          }
        }, 200);
      }
      if (isCtrl && event.key === 'k') {
        event.preventDefault();
        event.stopPropagation();
        onCtrlK?.();
      }
    };

    const handleKeyUp = (event) => {
      if (event.key === 'Control' || event.key === 'Meta') {
        ctrlPressedAlone = false;
        clearTimeout(ctrlTimer);
      }
    };

    window.addEventListener('keydown', handleKeyDown, { passive: false });
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      clearTimeout(ctrlTimer);
    };
  }, [onSlashPress, onCtrlN, onCtrlR, onCtrlSpace, onCtrlOnly, onCtrlK]);
}

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("FishyAIDB", 3);
    
    request.onerror = (event) => {
      reject("Database error: " + event.target.errorCode);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      let store;
      
      if (!db.objectStoreNames.contains("chats")) {
        store = db.createObjectStore("chats", { keyPath: "id" });
      } else {
        store = event.target.transaction.objectStore("chats");
      }
      
      if (!store.indexNames.contains("lastUsed")) {
        store.createIndex("lastUsed", "lastUsed", { unique: false });
      }
    };
    
    request.onsuccess = (event) => {
      resolve(event.target.result);
    };
  });
};

const saveChatsToDB = async (chats) => {
  try {
    const db = await openDB();
    const tx = db.transaction("chats", "readwrite");
    const store = tx.objectStore("chats");
    
    await store.clear();
    
    chats.forEach(chat => {
      store.add(chat);
    });
    
    return new Promise((resolve) => {
      tx.oncomplete = resolve;
    });
  } catch (err) {
    console.error("Failed to save chats to DB:", err);
  }
};

const loadChatsFromDB = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction("chats", "readonly");
    const store = tx.objectStore("chats");
    
    let request;
    if (store.indexNames.contains("lastUsed")) {
      const index = store.index("lastUsed");
      request = index.getAll();
    } else {
      request = store.getAll();
    }

    return new Promise((resolve) => {
      request.onsuccess = (event) => {
        const chats = event.target.result || [];
        resolve(chats.sort((a, b) => new Date(b.lastUsed) - new Date(a.lastUsed)));
      };
    });
  } catch (err) {
    console.error("Failed to load chats from DB:", err);
    return [];
  }
};

async function getcountrycode() {
  return fetch("https://ipapi.co/json/")
    .then((res) => res.json())
    .then((data) => {
      return data.country_code.toLowerCase();
    })
    .catch((err) => {
      console.error(`Failed to get country code: ${err}`);
      return null;
    });
}

const getInitialCountryCode = () => {
  const code = localStorage.getItem('country_code');
  if (code === null || code.trim() === '') return null;
  return code;
};

function App() {
  const timebeforeloginmodal = 2000;
  const TimesRequiredForDevMode = 5
  const colorList = [
  "#ff5252", // Red
  "#00c853", // Green
  "#40c4ff", // Light Blue
  "#ffd740", // Amber
  "#7c4dff", // Deep Purple
  "#ff6d00", // Orange
  "#1de9b6", // Teal
  "#d500f9", // Purple
  "#ff4081", // Pink
  "#00b0ff", // Sky Blue
  "#69f0ae", // Mint Green
  "#ffe57f", // Light Yellow
  "#f50057", // Hot Pink
  "#536dfe", // Indigo
  "#64dd17", // Lime Green
  ];

  const [builds, SetBuilds] = useState({ "APP_VERSION": null, "APP_VERSION_PRETTY": null, "APP_BUILD_DATE": null, "APP_BUILD_DATE_PRETTY": null });
  const { addToast } = useToast();

  const getRandomColor = () => colorList[Math.floor(Math.random() * colorList.length)];
  const [Acknowledges, setAcknowledges] = useState([]);
  const [IsResponding, setIsResponding] = useState(false);
  const [IsShareModalOpen, setIsShareModalOpen] = useState(false);
  const [IsChatSharedModalOpen, setIsChatSharedModalOpen] = useState(false);
  const [SharedChatURL, setSharedChatURL] = useState(null);
  const [ChatToShare, setChatToShare] = useState({ uses: 5, anonymous: false });
  const hasInitialized = useRef(false);
  const [UserToken, setUserToken] = useState(null);
  const [isOledMode, setIsOledMode] = useState(localStorage.getItem('oledmode') === "true" ?? false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [LBURL, setLBURL] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const [iconSrc, setIconSrc] = useState("/fishyai.png");
  const [iconStyling, setIconStyling] = useState({});
  const [welcomeStyling, setWelcomeStyling] = useState({});
  const [chats, setChats] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [TrustedDomains, setTrustedDomains] = useState([])
  const [ToolsVisible, setToolsVisible] = useState(false);
  const [Tools, setTools] = useState([]);
  const [isDebugMode, setIsDebugMode] = useState(() => {
    const saved = localStorage.getItem('debugmode');
    return saved === 'true';
  });
const [allowEvents, setAllowEvents] = useState(() => {
  const saved = localStorage.getItem('allowEvents');
  return saved !== null ? saved === 'true' : true;
});
  const [touchStartX, setTouchStartX] = useState(null);
  const [swipePercentage, setSwipePercentage] = useState(0);
  const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showtosmodal, setShowTOSModal] = useState(true);
  const [showdownloadmodal, setShowDownloadModal] = useState(false);
  const [downloadPassword, setDownloadPassword] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showURLAlertModal, setShowURLAlertModal] = useState(false);
  const [AlertedURL, setAlertedURL] = useState(null);
  const [isVerifyingToken, setIsVerifyingToken] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [isAppInitialized, setIsAppInitialized] = useState(false);// eslint-disable-next-line
  const [sharedChatData, setSharedChatData] = useState(null);// eslint-disable-next-line
  const [showMergeButton, setShowMergeButton] = useState(false);// eslint-disable-next-line
  const [shareSessionId, setShareSessionId] = useState(null);
  const skipSupabaseSaveRef = useRef(false);
  const [regenerationData, setRegenerationData] = useState(null);
  const [AllowDownloadingCalculations, setAllowDownloadingCalculations] = useState(false);

  const [IntroducingToolsCard, setIntroducingToolsCard] = useState(false);

  const base_api_url = LBURL ?? (isDebugMode
    ? `http://localhost:4033/`
    : `https://api.fishyai.crickdevs.com/Main_Backend/`);
  const LoadBalancer_URL = isDebugMode
    ? 'http://localhost:2202'
    : 'https://fishy-ai-production.up.railway.app'

  const [currentMessage, setCurrentMessage] = useState("");
  const [saved_country_code, setSavedCountryCode] = useState(getInitialCountryCode);
  const [loading, setLoading] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const chatMessagesRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [searchStatus, setSearchStatus] = useState(null);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const [isClearAllModalOpen, setIsClearAllModalOpen] = useState(false);
  const [features, setFeatures] = useState({ search: false, attach: false, think: false });
  const [searchMode, setSearchMode] = useState(false);
  const [thinkMode, setThinkMode] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [version_clicked_times, setVersionClickedTimes] = useState(0);
  const [is_dev_mode, setIsDevMode] = useState(false);
  const [showdevmodal, setShowDevModal] = useState(false);
  const fileInputRef = useRef(null);
  const [fontSize, setFontSize] = useState(localStorage.getItem('font_size') || 'medium');
  const [availableModels, setAvailableModels] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAdvancedEmptyState, setShowAdvancedEmptyState] = useState(false);
  const [selectedModel, setSelectedModel] = useState({
    provider: null,
    openrouterModel: null
  });
  const [showMathsModal, setShowMathsModal] = useState(false);
const [calculationInput, setCalculationInput] = useState("");
const [variables, setVariables] = useState([{ name: "", value: "" }]);
const [calculationProgress, setCalculationProgress] = useState([]);
const [finalResult, setFinalResult] = useState(null);
const [isCalculating, setIsCalculating] = useState(false);
const [calculationHistory, setCalculationHistory] = useState([]);
const [showHistoryModal, setShowHistoryModal] = useState(false);// eslint-disable-next-line
const [isMerging, setIsMerging] = useState(false);

// Add function to fetch calculation history
const fetchCalculationHistory = async () => {
  try {
    const response = await fetch(`${base_api_url}api/functions/maths/v1/calculations`, {
      headers: {
        'Authorization': `Bearer ${UserToken}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }
    
    const data = await response.json();
    setCalculationHistory(data.data || []);
    setShowHistoryModal(true);
    setAllowDownloadingCalculations(data.allowDownloading)
  } catch (err) {
    console.error("Failed to fetch calculation history:", err);
    addToast({
      message: 'Failed to load calculation history',
      type: 'error',
      duration: 3000
    });
  }
};

// Add this function to view a specific calculation
const viewCalculation = (calculation) => {
  setCalculationInput(calculation.expression);
  
  // Parse variables if they exist in the response
  let parsedVariables = [];
  if (calculation.variables) {
    try {
      const vars = typeof calculation.variables === 'string' 
        ? JSON.parse(calculation.variables) 
        : calculation.variables;
      
      parsedVariables = Object.entries(vars).map(([name, value]) => ({
        name,
        value: String(value)
      }));
    } catch (e) {
      console.error("Failed to parse variables:", e);
    }
  }

  setVariables(parsedVariables.length ? parsedVariables : [{ name: "", value: "" }]);
  
  // Reset progress and result
  setCalculationProgress([]);
  setFinalResult(null);

  // Only show success result if calculation was successful
  if (calculation.success) {
    // Handle explanation which might be a string, array, or null
    let explanationArray = [];
    if (typeof calculation.explanation === 'string') {
      try {
        explanationArray = JSON.parse(calculation.explanation);
      } catch (e) {
        console.error("Failed to parse explanation string:", e);
      }
    } else if (Array.isArray(calculation.explanation)) {
      explanationArray = calculation.explanation;
    }

    setFinalResult({
      expression: calculation.expression,
      value: calculation.value,
      error: calculation.error,
      timeTaken: calculation.timeTaken,
      variables: parsedVariables,
      explanation: explanationArray
    });
  } else {
    // Show error message for failed calculations
    setCalculationProgress([{ 
      type: "error", 
      data: "Calculation failed" 
    }]);
  }
  
  setShowMathsModal(true);
  setShowHistoryModal(false);
};

// Add these functions to your App component
const addVariable = () => {
  setVariables([...variables, { name: "", value: "" }]);
};

const removeVariable = (index) => {
  if (variables.length <= 1) return;
  const newVariables = [...variables];
  newVariables.splice(index, 1);
  setVariables(newVariables);
};

const updateVariable = (index, field, value) => {
  const newVariables = [...variables];
  newVariables[index][field] = value;
  setVariables(newVariables);
};

const calculateMaths = async () => {
  setIsCalculating(true);
  setCalculationProgress([]);
  setFinalResult(null);
  
  try {
    // Prepare variables object (only include valid ones)
    const varsObj = {};
    variables.forEach(v => {
      if (v.name && v.value) {
        varsObj[v.name] = parseFloat(v.value);
      }
    });
    
    const body = {
      calculation: calculationInput,
      ...(Object.keys(varsObj).length > 0 && { variables: varsObj })
    };
    
    const response = await fetch(`${base_api_url}api/functions/maths/v1/calculations`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${UserToken}`,
      },
      body: JSON.stringify(body),
    });
    
    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }
    
    if (!response.body) {
      throw new Error("No response body");
    }
    
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      
      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n').filter(line => line.trim() !== '');
      
      for (const line of lines) {
        try {
          const parsed = JSON.parse(line);
          
          if (parsed.progress) {
            setCalculationProgress(prev => [...prev, { type: "progress", data: parsed.progress }]);
          } else if (parsed.final) {
            setFinalResult(parsed.final);
            setCalculationProgress(prev => [...prev, { type: "result", data: parsed.final }]);
          } else if (parsed.allowDownloading) {
            setAllowDownloadingCalculations(parsed.allowDownloading);
          }
        } catch (err) {
          console.error("Error parsing response:", err);
        }
      }
    }
  } catch (err) {
    setCalculationProgress(prev => [...prev, { 
      type: "error", 
      data: `Error: ${err.message || "Request failed"}` 
    }]);
  } finally {
    setIsCalculating(false);
    addToast({
      message: 'Calculation Completed',
      type: 'success',
      duration: 3000
    });
  }
};

const downloadCalculation = () => {
  if (!finalResult) return;

const content = `
Calculation: ${finalResult.expression}
Result: ${finalResult.value}
Time Taken: ${finalResult.timeTaken}
${finalResult.explanation && finalResult.explanation.length > 0 ? `Explanation:\n${finalResult.explanation.join('\n')}` : ''}
`.trim();

  
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `calculation-${Date.now()}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('FishyAI_Theme') || "auto";
  });
  const [showLoader, setShowLoader] = useState(true);

  useEffect(() => {
    if (!is_dev_mode) return;
    const Urls = {BaseURL: base_api_url, LoadBalancer_URL: LoadBalancer_URL}
    console.log(Urls)
  }, [base_api_url, LoadBalancer_URL, is_dev_mode]);

  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => {
        setShowLoader(false);
      }, 500);
    } else {
      setShowLoader(true);
    }
  }, [isLoading]);

  const loadSharedChat = useCallback(async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const shareId = urlParams.get('shareid');
  
  if (!shareId) return;
  
  try {
    const response = await fetch(`${base_api_url}share/${shareId}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      addToast({
        message: errorData.message || 'Failed to load shared chat',
        type: 'error',
        duration: 5000
      });
      return;
    }
    
    const data = await response.json();
    
    // Set the shared chat data
    setSharedChatData(data);
    setShareSessionId(data.sessionId); // Store session ID for later merge
    
    // Create a new chat with shared messages
    const sharedChat = {
      id: uuidv4(),
      title: data.anonymous ? "Shared Chat" : `${data.author}'s Chat`,
      messages: data.messages,
      createdAt: new Date().toISOString(),
      lastUsed: new Date().toISOString(),
      isShared: true
    };
    
    // Add to chats and set as current
    setChats(prev => [sharedChat, ...prev]);
    setCurrentChatId(sharedChat.id);
    
    // Show merge button if user is logged in
    if (user) {
      setShowMergeButton(true);
    }
    
    addToast({
      message: `Loaded shared chat${!data.anonymous ? ` from ${data.author}` : ''}`,
      type: 'success',
      duration: 3000
    });
    
  } catch (error) {
    console.error('Error loading shared chat:', error);
    addToast({
      message: 'Failed to load shared chat',
      type: 'error',
      duration: 5000
    });
  }
}, [base_api_url, user, addToast, setChats, setCurrentChatId]);

// Function to merge shared chat to user's account
const mergeSharedChat = async () => {
  if (!shareSessionId || !user) return;
  
  try {
    const response = await fetch(`${base_api_url}account/chats/merge/${shareSessionId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${UserToken}`,
        'x-sessionid': shareSessionId
      }
    });
    
    const data = await response.json();
    
    if (data.success) {
      addToast({
        message: 'Chat successfully merged to your account',
        type: 'success',
        duration: 3000
      });
      
      setShowMergeButton(false);
      
      // Reload the page to refresh data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } else {
      addToast({
        message: 'Failed to merge chat',
        type: 'error',
        duration: 3000
      });
    }
    
  } catch (error) {
    console.error('Error merging shared chat:', error);
    addToast({
      message: 'Failed to merge chat',
      type: 'error',
      duration: 5000
    });
  }
};

  const deleteChatFromServer = async (chatId) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${base_api_url}account/delete-chat?id=${chatId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete chat on server');
      }
    } catch (err) {
      console.error('Error deleting chat from server:', err);
      throw err;
    }
  };



const MarkdownRenderer = ({ content }) => {
  const renderer = new marked.Renderer();

  renderer.link = (href, title, text) => {
    const isTrusted =
      typeof href.href === 'string' &&
      (() => {
        try {
          const { hostname } = new URL(href.href);
          return TrustedDomains.some(
            domain => hostname === domain || hostname.endsWith(`.${domain}`)
          );
        } catch {
          return false; // invalid URL
        }
      })();

    if (isTrusted) {
      return `<a href="${href.href}" target="_blank" rel="noopener noreferrer">${href.text}</a>`;
    } else {

// later in JS:
document.addEventListener("click", (e) => {
  if (e.target.closest(".unsafe-link")) {
    e.preventDefault();
    const url = e.target.closest(".unsafe-link").dataset.url;
    setShowURLAlertModal(true);
    setAlertedURL(url);
  }
});
      return `
  <a href="${href.href}" 
     target="_blank" 
     rel="noopener noreferrer"
     class="unsafe-link"
     data-url="${href.href}">
    ${href.text} <span class="link-warning">(unsafe)</span>
  </a>
`;

    }
  };

  return (
    <div
      dangerouslySetInnerHTML={{
        __html: marked.parse(content, { renderer }),
      }}
    />
  );
};

async function getTrustedDomains() {
const DefaulttrustedDomains = [
  'fishyai.crickdevs.com',
  'google.com',
  'openai.com',
  'gemini.google.com',
  'htdevs.pages.dev'
];
  try {
  var response = await fetch(`${base_api_url}trusted-domains`);
  var data = await response.json();
  return data.domains || DefaulttrustedDomains;
  } catch (error) {
    console.error('Error fetching trusted domains:', error);
    return DefaulttrustedDomains;
  }
}

  const saveChatsToSupabase = useCallback(async (chats) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    // eslint-disable-next-line
    if(true === true) {
      return;
    }

    try {
      const response = await fetch(`${base_api_url}account/chats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ chats })
      });

      if (!response.ok) {
        const errorText = await response.text();
        if (response.status === 413) {
          throw new Error('Payload too large');
        }
        throw new Error(`Server error: ${response.status} ${errorText}`);
      }
    } catch (err) {
      console.error('Error saving chats to Supabase:', err);
      if (err.message === 'Payload too large') {
        addToast({
          message: 'Your chat history is too large. Please delete some chats to save to cloud.',
          type: 'error',
          duration: 5000
        });
      }
    }
  }, [base_api_url, addToast]);

const loadChatsFromSupabase = useCallback(async () => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  try {
    const response = await fetch(`${base_api_url}account/chats`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      console.error('Failed to load chats:', response.status, response.statusText);
      return null;
    }

    const data = await response.json();
    console.log('Raw chat data from server:', data); // Debug log
    
    // The server returns the array directly, so just return it
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('Error loading chats from Supabase:', err);
    return null;
  }
}, [base_api_url]);


useEffect(() => {
  if (!ToolsVisible) return;

  async function fetchTools() {
    try {
      const tools = await GetTools();
      setTools(tools);
    } catch (err) {
      console.error("Failed to fetch tools:", err);
    }
  }

  fetchTools();// eslint-disable-next-line
}, [ToolsVisible]);


  useEffect(() => {
    if (!isAppInitialized) return;

    // Only save to local DB if user is not logged in
    if (!user) {
      saveChatsToDB(chats);
    }

    if (user && !skipSupabaseSaveRef.current) {
      saveChatsToSupabase(chats);
    }

    skipSupabaseSaveRef.current = false;
  }, [chats, user, isAppInitialized, saveChatsToSupabase]);

  useEffect(() => {
    saveChatsToDB(chats);
  }, [chats]);

  function setEvent(EventName) {
    if (!allowEvents) return;
    if(String(EventName).toLowerCase() === 'hitman') {
      setIconSrc("/events/diana_burnwood.png");
      setIconStyling({ marginBottom: '20px', width: 75, borderRadius: '50%' });
      if(Math.random() < 0.1) {
      setWelcomeStyling({ color: '#A71D2A' });
      }
    } else if (String(EventName).toLowerCase() === 'ironman') {
      setIconSrc("/events/jarvis.png");
      setIconStyling({ marginBottom: '20px', width: 150 });
    } else if (String(EventName).toLowerCase() === 'miawinters') {
      setIconSrc("/events/Mia-Winters.webp");
      setIconStyling({ marginBottom: '20px', width: 100, borderRadius: '50%' });
    }
  }

  useEffect(() => {
    const handlePaste = async (event) => {
      if (document.activeElement.tagName === 'INPUT' || 
          document.activeElement.tagName === 'TEXTAREA') {
        return;
      }

      const items = event.clipboardData.items;
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
      const newFiles = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        if (item.kind === 'file' && validTypes.includes(item.type)) {
          try {
            const blob = item.getAsFile();
            if (!blob) continue;
            
            if (blob.size > 10 * 1024 * 1024) {
              addToast({
                message: `Image is too large (max 10MB)`,
                type: 'error',
                duration: 3000
              });
              continue;
            }
            
            const fileName = `pasted-image-${Date.now()}.${item.type.split('/')[1]}`;
            const file = new File([blob], fileName, { type: item.type });
            newFiles.push(file);
            
          } catch (err) {
            console.error('Error processing pasted file:', err);
          }
        }
      }

      if (newFiles.length > 0) {
        event.preventDefault();
        
        if (attachedFiles.length + newFiles.length > 3) {
          addToast({
            message: 'Cannot attach more than 3 files',
            type: 'error',
            duration: 3000
          });
          return;
        }
        
        setAttachedFiles(prev => [...prev, ...newFiles]);
        
        addToast({
          message: `Added ${newFiles.length} image${newFiles.length > 1 ? 's' : ''} from clipboard`,
          type: 'success',
          duration: 3000
        });
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [attachedFiles, addToast]);

function SetShowIntroducingCard(show, AcknowledgeKey) {
  if (AcknowledgeKey === 'IntroducingTools') {
    setIntroducingToolsCard(show);
  }
}

// function SetShowTool(show, ToolKey) {
//   if (ToolKey === 'maths') {
//   }
// }

async function Acknowledge(Acknowledged = false, AcknowledgeKey, token) {
    if(Acknowledged === false) {
      SetShowIntroducingCard(true, AcknowledgeKey);
  } else {
const options = {
  method: "POST",
  headers: {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ acknowledge: AcknowledgeKey }), // ✅ match backend
};

const response = await fetch(`${base_api_url}account/requirements`, options); // ✅ match backend
const data = await response.json();
setAcknowledges(data.data);
  }
}

async function GetRequirements(token) {
  try {
    const options = {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    const response = await fetch(`${base_api_url}account/requirements`, options);
    const data = await response.json();
    setAcknowledges(data);
  } catch (error) {
    console.error("Error fetching account requirements:", error);
  }
}

/**
 * Fetches, filters, and returns status updates, with an option to display toasts.
 * @param {boolean} showToastNotifications If true, displays a toast for each update.
 * @param {function} addToast The toast function to display notifications.
 * @returns {Promise<Array<object>>} An array of filtered status updates.
 */
async function getFishyAIStatusUpdates(showToastNotifications = false) {
    const statusApiUrl = 'https://status.crickdevs.com/api/v1/summary';
    let statusUpdates = [];

    try {
        const response = await fetch(statusApiUrl);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const statusData = await response.json();

        const hasFishyAI = (event) => event.affected_components.some(component => component.group_name && component.group_name.includes('FishyAI'));

        // Process ongoing incidents
        if (statusData.ongoing_incidents) {
            statusData.ongoing_incidents.forEach(incident => {
                if (hasFishyAI(incident)) {
                    let impact = 'info';
                    // eslint-disable-next-line
                    switch (incident.current_worst_impact) {
                        case 'degraded_performance':
                        case 'partial_outage':
                            impact = 'warning';
                            break;
                        case 'full_outage':
                            impact = 'error';
                            break;
                    }
                    statusUpdates.push({ ...incident, impact });
                }
            });
        }

        // Process in-progress maintenances
        if (statusData.in_progress_maintenances) {
            statusData.in_progress_maintenances.forEach(maintenance => {
                if (hasFishyAI(maintenance)) {
                    statusUpdates.push({ ...maintenance, impact: 'warning' });
                }
            });
        }

        // Process scheduled maintenances
        if (statusData.scheduled_maintenances) {
            statusData.scheduled_maintenances.forEach(maintenance => {
                if (hasFishyAI(maintenance)) {
                    statusUpdates.push({ ...maintenance, impact: 'info' });
                }
            });
        }

        // Show toasts if requested
        if (showToastNotifications) {
            if (statusUpdates.length > 0) {
                    addToast({
                        autoClose: true,
                        message: `Status: ${statusUpdates[0].name}`,
                        link: statusUpdates[0].url,
                        type: statusUpdates[0].impact ?? 'Warning',
                        duration: 7500
                    });
            } else {
                //addToast({
                //    autoClose: false,
                //    message: 'No FishyAI incidents or maintenances found.',
                //    type: 'info' ?? 'Warning',
                //    duration: 3000
                //});
            }
        }

    } catch (error) {
        console.error('Failed to fetch status data:', error);
        //addToast({
        //    autoClose: false,
        //    message: 'Failed to retrieve status data. Please try again later.',
        //    type: 'error' ?? 'Warning',
        //    duration: 3000
        //});
        return [];
    }

    return statusUpdates;
}

const verifyToken = useCallback(async (token, show_login = false, ClearChatsIfNotLogged = true) => {
  setIsVerifyingToken(true);
  try {
    const userTime = new Date().toLocaleString();
    const response = await fetch(`${base_api_url}account/information`, {
      headers: { 'Authorization': `Bearer ${token}`, 'X-User-Time': userTime, 'X-Events': allowEvents },
    });

    if (response.ok) {
      const userData = await response.json();
      setUserToken(token);
      if(userData.phrase && document.getElementById('welcome-message')) {
        await GetRequirements(token);
        document.getElementById('welcome-message').innerText = userData.phrase;
      }
      setUser(userData);
      localStorage.setItem('token', token);

      // FIX: Load chats after setting user
      const supabaseChats = await loadChatsFromSupabase();
      console.log('Loaded chats:', supabaseChats); // Debug log
      
      if (supabaseChats && Array.isArray(supabaseChats) && supabaseChats.length > 0) {
        skipSupabaseSaveRef.current = true;
        setChats(supabaseChats);
        console.log('Set chats:', supabaseChats); // Debug log
      } else {
        console.log('No chats loaded from server, loading local chats');
        const savedChats = await loadChatsFromDB();
        setChats(savedChats);
      }

      const url = new URL(window.location);
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url);
    } else {
      // Handle non-logged in user
      if (ClearChatsIfNotLogged === false) {
        const savedChats = await loadChatsFromDB();
        setChats(savedChats);
      } else {
        setChats([]);
      }
      throw new Error('Token verification failed');
    }
  } catch (error) {
    console.error('Token verification error:', error);
    setAuthError('Failed to verify token. Please login again.');
    localStorage.removeItem('token');
  } finally {
    setIsVerifyingToken(false);
  }// eslint-disable-next-line
}, [base_api_url, loadChatsFromSupabase, setAuthError, setIsVerifyingToken, setUser, setChats]);

  const getRandomExamples = useCallback(async () => {
    try {
      const screenW = window.screen?.width ?? 0;
      const screenH = window.screen?.height ?? 0;
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}`, 'X-Events': allowEvents, 'X-ScreenY' : screenH, 'X-ScreenX' : screenW } : {};
      const res = await fetch(`${base_api_url}suggestions`, { headers });
      if (res.ok) {
      const data = await res.json();
      var examples_lenght_now = data.ExamplesShown;
      if(data.Hitman === true) {
        setEvent("hitman");
      } else if (data.IronMan === true) {
        setEvent("ironman");
      } else if (data.MiaWinters === true) {
        setEvent("miawinters");
      }
      const shuffled = [...data.examplePrompts].sort(() => 0.5 - Math.random());
      return shuffled.slice(0, examples_lenght_now);
      }
    } catch (err) {
      console.log("Using fallback examples");
    }
    
    return [
      "Quantum computing basics",
      "Blockchain technology explained",
      "Machine learning fundamentals"
    ];// eslint-disable-next-line
  }, [base_api_url, allowEvents]);

  const [randomExamples, setRandomExamples] = useState([]);
  
  const currentChat = chats.find(chat => chat.id === currentChatId) || null;
  const messages = useMemo(() => {
    const chat = chats.find(chat => chat.id === currentChatId);
    return chat ? chat.messages : [];
  }, [chats, currentChatId]);

  useEffect(() => {
    (async () => {
      if (saved_country_code === null && sessionStorage.getItem('country_code') === null) {
        const cc = await getcountrycode();
        setSavedCountryCode(cc);
        sessionStorage.setItem('country_code', cc);
        const input = document.getElementById('countrycodeinput');
        if (input) input.value = cc;
      } else if (saved_country_code === null && sessionStorage.getItem('country_code') !== null) {
        const storedCode = sessionStorage.getItem('country_code');
        setSavedCountryCode(storedCode);
        const input = document.getElementById('countrycodeinput');
        if (input) input.value = storedCode;
      }
    })();
  }, [saved_country_code]);

  const clearAllChats = async () => {
    try {
      // Clear from server if user is logged in
if (user) {
  const token = localStorage.getItem('token');
  const response = await fetch(`${base_api_url}account/chats/clear`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  // Await once to parse JSON
  const data = await response.json();

  if (data.message) {
    addToast({
      message: data.message,
      type: data.type ?? 'info',
      duration: 3000
    });
  }

  if (!response.ok) {
    throw new Error('Failed to clear chats on server');
  }
}

      // Clear local DB
      const db = await openDB();
      const tx = db.transaction("chats", "readwrite");
      const store = tx.objectStore("chats");
      await store.clear();
      
      setChats([]);
      setCurrentChatId(null);
      
      // Reload page if user is not logged in
      if (!user) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (err) {
      console.error("Failed to clear chats:", err);
    }
  };

  const clearAllData = async () => {
    try {
      const db = await openDB();
      const tx = db.transaction("chats", "readwrite");
      const store = tx.objectStore("chats");
      await store.clear();
      
      setChats([]);
      setCurrentChatId(null);
      localStorage.clear();
      sessionStorage.clear();
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error("Failed to clear chats:", err);
    }
  };

  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  useEffect(() => {
    if (!shouldAutoScroll || !messagesEndRef.current) return;
    
    messagesEndRef.current.scrollIntoView({
      behavior: "smooth",
      block: "nearest"
    });
  }, [messages, shouldAutoScroll]);

  useEffect(() => {
    const messagesContainer = chatMessagesRef.current;
    if (!messagesContainer) return;

    const checkScrollPosition = () => {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      setShouldAutoScroll(scrollHeight - scrollTop <= clientHeight + 50);
    };

    messagesContainer.addEventListener('scroll', checkScrollPosition);
    return () => messagesContainer.removeEventListener('scroll', checkScrollPosition);
  }, []);

  useEffect(() => {
    const fetchExamples = async () => {
      const examples = await getRandomExamples();
      setRandomExamples(examples);
    };
    
    fetchExamples();
  }, [getRandomExamples]);

  const updateUrlParam = (chatId) => {
    const params = new URLSearchParams(window.location.search);
    params.set("chat", chatId);
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({ chatId }, "", newUrl);
  };

  const activeStreamRef = useRef(null);

  const createNewChat = () => {
    if (activeStreamRef.current) {
      activeStreamRef.current.abort();
    }
    
    const newChatId = uuidv4();
    const newChat = {
      id: newChatId,
      title: "New Chat",
      messages: [],
      createdAt: new Date().toISOString(),
      lastUsed: new Date().toISOString()
    };
    
    setChats(prev => {
      const updatedChats = [newChat, ...prev];
      updatedChats.sort((a, b) => new Date(b.lastUsed) - new Date(a.lastUsed));
      return updatedChats;
    });
    
    setCurrentChatId(newChatId);
    updateUrlParam(newChatId);
    setSidebarOpen(false);
  };

  const handleSubmit = async (e, userContent = currentMessage, userFiles = attachedFiles) => {
    if (e?.preventDefault) e.preventDefault(); // only call if it's an event
    console.log(`currentChatId: ${currentChatId}, currentMessage: ${currentMessage}, attachedFiles: ${attachedFiles}, regenerationData: ${JSON.stringify(regenerationData)}`);
    let cc = saved_country_code ?? null;
    
    if (regenerationData && regenerationData.chatId === currentChatId) {
      userContent = regenerationData.userMessage.content;
      userFiles = regenerationData.userMessage.files || [];
      setRegenerationData(null);
    }
    
    if (!userContent.trim() && userFiles.length === 0) return;
    
    setLoading(true);
    setSearchStatus(null);
    setCurrentMessage("");
    
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
    
    let chatId = currentChatId;
    if (!currentChatId) {
      const newChatId = uuidv4();
      const newChat = {
        id: newChatId,
        title: userContent.substring(0, 30) + (userContent.length > 30 ? "..." : ""),
        messages: [],
        createdAt: new Date().toISOString(),
        lastUsed: new Date().toISOString()
      };
      setChats(prev => {
        const updatedChats = [newChat, ...prev];
        updatedChats.sort((a, b) => new Date(b.lastUsed) - new Date(a.lastUsed));
        return updatedChats;
      });
      setCurrentChatId(newChatId);
      updateUrlParam(newChatId);
      chatId = newChatId;
    } else {
      setChats(prev => {
        const updatedChats = prev.map(chat => 
          chat.id === chatId 
            ? { ...chat, lastUsed: new Date().toISOString() } 
            : chat
        );
        return updatedChats.sort((a, b) => new Date(b.lastUsed) - new Date(a.lastUsed));
      });
    }
    
    const filesMetadata = userFiles.map(file => ({
      name: file.name,
      type: file.type,
      size: file.size
    }));
    
    const userMessage = {
      id: Date.now() + 1,
      role: "user",
      content: userContent,
      timestamp: new Date().toISOString(),
      files: filesMetadata
    };
    
    const tempAssistantMessage = {
      id: Date.now() + 2,
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
      isStreaming: true
    };
    
    setChats(prev => {
      const updatedChats = prev.map(chat => 
        chat.id === chatId 
          ? { 
              ...chat, 
              title: chat.messages.length === 0 
                ? userContent.substring(0, 30) + (userContent.length > 30 ? "..." : "") 
                : chat.title,
              messages: [...chat.messages, userMessage, tempAssistantMessage],
              lastUsed: new Date().toISOString()
            } 
          : chat
      );
      return updatedChats.sort((a, b) => new Date(b.lastUsed) - new Date(a.lastUsed));
    });
    
    setCurrentMessage("");
    setAttachedFiles([]);
    setShouldAutoScroll(true);
    
    try {
      setIsResponding(true)
      const controller = new AbortController();
      activeStreamRef.current = controller;
      
      const formData = new FormData();
      formData.append('text', userContent);
      formData.append('model', selectedModel.provider);
      if (selectedModel.provider === 'openrouter' && selectedModel.openrouterModel) {
        formData.append('openrouter_model', selectedModel.openrouterModel);
      }
      formData.append('mode', "Balanced");
      if(cc !== null) {
        formData.append('cc', cc);
      }
      formData.append('chatHistory', JSON.stringify(
        messages.filter(msg => msg.role === "user" || !msg.isStreaming)
      ));
      formData.append('devmode', is_dev_mode ?? false)
      formData.append('search', searchMode);
      formData.append('think', thinkMode);
      formData.append('chatId', chatId);
        
      userFiles.forEach(file => {
        formData.append('files', file);
      });

      const token = localStorage.getItem('token') || null;
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${base_api_url}chat`, {
        method: "POST",
        body: formData,
        headers,
        signal: controller.signal
      });

      if (!res.ok) {
        if (res.status === 413) {
          throw new Error("File size too large. Please use files smaller than 10MB.");
        }
        throw new Error(`Server error: ${res.status}`);
      }
      
      if (!res.body) throw new Error("No response body");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let localExplanation = "";
      let localIsFirstChunk = true;
      
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter(line => line.trim() !== '');
        
        for (const line of lines) {
          try {
            const parsed = JSON.parse(line);
            
            if (parsed.status) {
              setSearchStatus(parsed.status);
            } 
            else if (parsed.data) {
              localExplanation += parsed.data;
              const currentExplanation = localExplanation;
              
              setChats(prev => prev.map(chat => 
                chat.id === chatId 
                  ? { 
                      ...chat, 
                      messages: chat.messages.map(msg => 
                        msg.id === tempAssistantMessage.id 
                          ? { ...msg, content: currentExplanation }
                          : msg
                      )
                    } 
                  : chat
              ));
              
              if (localIsFirstChunk) {
                setSearchStatus(null);
                localIsFirstChunk = false;
              }
            }
          } catch (err) {
            console.error("Error parsing response:", err);
            setIsResponding(false)
          }
        }
      }
      
      setChats(prev => prev.map(chat => 
        chat.id === chatId 
          ? { 
              ...chat, 
              messages: chat.messages.map(msg => 
                msg.id === tempAssistantMessage.id 
                  ? { ...msg, isStreaming: false } 
                  : msg
              )
            } 
          : chat
      ));

    } catch (err) {
      if (String(err.error).includes('signal is aborted without reason') || String(err.message).includes('signal is aborted without reason') || err.error === 'signal is aborted without reason' || String(err.message).includes('signal is aborted without reason')) {
        setIsResponding(false)
        return;
      }
      if (String(err.error).includes('BodyStreamBuffer was aborted') || String(err.message).includes('BodyStreamBuffer was aborted') || err.error === 'BodyStreamBuffer was aborted' || String(err.message).includes('BodyStreamBuffer was aborted')) {
      setChats(prev => prev.map(chat => 
        chat.id === chatId 
          ? { 
              ...chat, 
              messages: chat.messages.map(msg => 
                msg.id === tempAssistantMessage.id 
                  ? { 
                      ...msg, 
                      content: `<span style="color: #ff4d4f">Cancelled by user</span>`, 
                      isStreaming: false 
                    } 
                  : msg
              )
            } 
          : chat
      ));
      } else {
      setChats(prev => prev.map(chat => 
        chat.id === chatId 
          ? { 
              ...chat, 
              messages: chat.messages.map(msg => 
                msg.id === tempAssistantMessage.id 
                  ? { 
                      ...msg, 
                      content: `<span style="color: #ff4d4f">Error: ${err.error || err.message || "Request failed"}</span>`, 
                      isStreaming: false 
                    } 
                  : msg
              )
            } 
          : chat
      ));
      addToast({ message: err.message || "Request failed", type: "error", duration: 5000 });
    }
    setIsResponding(false)
    } finally {
      setLoading(false);
      setSearchStatus(null);
      setIsResponding(false)
      activeStreamRef.current = null;
    }
  };

  const cancelGeneration = () => {
    if (activeStreamRef.current) {
      activeStreamRef.current.abort();
      activeStreamRef.current = null;
      setLoading(false);
      setSearchStatus(null);
    }
  };

  const regenerateResponse = async () => {
    if (!currentChat || currentChat.messages.length === 0) return;

    const messages = [...currentChat.messages];
    const lastMessage = messages[messages.length - 1];
    
    if (lastMessage.role !== "assistant") return;
    
    let userMessage = null;
    for (let i = messages.length - 2; i >= 0; i--) {
      if (messages[i].role === "user") {
        userMessage = messages[i];
        break;
      }
    }
    
    if (!userMessage) return;
    
    const newMessages = messages.filter(
      msg => msg.role !== "assistant" || msg.id !== lastMessage.id
    );
    
    setChats(prev => prev.map(chat => 
      chat.id === currentChatId 
        ? { ...chat, messages: newMessages } 
        : chat
    ));
    
    setRegenerationData({
      chatId: currentChatId,
      userMessage: userMessage
    });

    setTimeout(() => {
      document.querySelector(".input-form")?.dispatchEvent(
        new Event("submit", { bubbles: true, cancelable: true })
      );
    }, 100);
  };

  const deleteChat = async (chatId) => {
    if (user) {
      try {
        await deleteChatFromServer(chatId);
      } catch (err) {
        addToast({ message: 'Failed to delete chat on server', type: 'error', duration: 5000 });
        return;
      }
    }

    // Delete from local state
    setChats(prev => prev.filter(chat => chat.id !== chatId));
    if (currentChatId === chatId) {
      setCurrentChatId(null);
    }

    // Delete from local DB only if user is not logged in
    if (!user) {
      try {
        const db = await openDB();
        const tx = db.transaction("chats", "readwrite");
        const store = tx.objectStore("chats");
        await store.delete(chatId);
      } catch (err) {
        console.error("Failed to delete chat from local DB:", err);
      }
    }

    setIsDeleteModalOpen(false);
  };

const shareChat = async (conversationId, uses = null, anonymous = false) => {
  if (!conversationId) {
    addToast({ message: "No conversation selected", type: "error", duration: 5000 });
    return;
  }

  try {
    const payload = {
      conversation_id: conversationId,
      ...(uses != null ? { uses } : {}),
      anonymous
    };

    const response = await fetch(`${base_api_url}account/chat/share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(payload)
    });

    if (response.ok) {
      const data = await response.json();
      setSharedChatURL(data.link);
      setIsShareModalOpen(false);
      setIsChatSharedModalOpen(true);
      return data;
    } else {
      const errData = await response.json();
      throw new Error(errData.message || 'Failed to share chat');
    }
  } catch (err) {
    addToast({ message: err.message || "Request failed", type: "error", duration: 5000 });
  }
};

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        alert(`File ${file.name} is too large (max 10MB)`);
        return false;
      }
      return true;
    });
    
    setAttachedFiles(prev => [...prev, ...validFiles]);
    e.target.value = null;
  };

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // const mergeChats = (localChats, serverChats) => {
  //   const chatMap = new Map();
    
  //   localChats.forEach(chat => {
  //     chatMap.set(chat.id, chat);
  //   });
    
  //   serverChats.forEach(chat => {
  //     const existing = chatMap.get(chat.id);
  //     if (!existing || new Date(chat.lastUsed) > new Date(existing.lastUsed)) {
  //       chatMap.set(chat.id, chat);
  //     }
  //   });
    
  //   return Array.from(chatMap.values()).sort((a, b) => 
  //     new Date(b.lastUsed) - new Date(a.lastUsed)
  //   );
  // };

  const click_version = () => {
    const isDevMode = localStorage.getItem("dev_mode") === "true";

    if (version_clicked_times >= TimesRequiredForDevMode || isDevMode) {
      addToast({
        message: 'You are in dev mode',
        type: 'info',
        duration: 3000
      });

      if (!isDevMode) {
        setShowDevModal(true);
      }
    }

    setVersionClickedTimes(version_clicked_times + 1);
  };

  const enable_devmode = () => {
    localStorage.setItem("dev_mode", "true");
    setIsDevMode(true);
  }

  const disable_devmode = () => {
    localStorage.setItem("dev_mode", "false");
    setVersionClickedTimes(0);
    setIsDebugMode(false);
    setIsDevMode(false);
    localStorage.setItem('debugmode', false);
    addToast({
      message: 'Dev mode disabled',
      type: 'info',
      duration: 3000
    });
  }

  const GetBuilds = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(`${base_api_url}builds`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        SetBuilds(data);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.error('GetBuilds request timed out after 15s');
      } else {
        console.error('Failed to initialize app', err);
      }
    }
  }, [base_api_url]);

  const Wait_Seconds = useCallback(async (seconds) => {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve();
      }, seconds * 1000);
    });
  }, []);

async function GetTools(AddIconsToMemory = true) {
  const options = {method: 'GET'};

  return fetch(`${base_api_url}api/functions/tools`, options)
    .then(response => response.json())
    .then(response => {
      // Change from response.tools to response.Tools
      setTools(response.Tools);
      if(AddIconsToMemory === true) {
      response.Tools.forEach(tool => {
        const icon = new Image();
        icon.src = tool.icon
      })
    }
      return response.Tools;
    })
    .catch(err => console.error(err));
}

const initializeApp = useCallback(async () => {
  if (hasInitialized.current) return;
  hasInitialized.current = true;
  if (isAppInitialized) return;
  const params = new URLSearchParams(window.location.search);

    const tokenParam = params.get('token');
    const themeParam = params.get('theme');
    const promptParam = params.get('prompt');
    const chatParam = params.get('chat'); // eslint-disable-next-line
    const shareIdParam = params.get('shareid'); // Add this line
    const storedToken = localStorage.getItem('token');
  
  setAlertedURL(null);
  await Wait_Seconds(1.5);

  await GetLBURL();

  setIsLoading(true)
  await GetBuilds();
  var domains = await getTrustedDomains()
  setTrustedDomains(domains)

  var isdevmode = localStorage.getItem("dev_mode");
  setIsDevMode(isdevmode === "true");

  await GetTools();

  try {
    const advancedRes = await fetch(`${base_api_url}advanced_page`);
    if (advancedRes.ok) {
      const advancedData = await advancedRes.json();
      setShowAdvancedEmptyState(advancedData.use_advanced_page);
    }
    
    if(themeParam) {
      localStorage.setItem('FishyAI_Theme', themeParam);
      setTheme(themeParam);
    }

    if (tokenParam) {
      await verifyToken(tokenParam, true, true);
    } else if (storedToken) {
      await verifyToken(storedToken, true, true);
    } else {
      setChats([]);
      // Add user's local time as a header
      const userTime = new Date().toLocaleString();
      const getphrase = await fetch(`${base_api_url}account/welcome`, {
        headers: {
          'X-User-Time': userTime, 
          'X-Events': allowEvents 
        }
      });
      if (getphrase.ok) {
        const phrase = await getphrase.json();
        if (document.getElementById('welcome-message')) {
          document.getElementById('welcome-message').innerText = phrase.phrase;
        }
        if(phrase.Hitman === true) {
          setEvent("hitman");
        } else if (phrase.IronMan === true) {
          setEvent("ironman");
        } else if (phrase.MiaWinters === true) {
          setEvent("miawinters");
        }
      }
              setTimeout(() => {
        setShowLoginModal(true);
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }, timebeforeloginmodal);
    }



const loadedChats = await loadChatsFromSupabase();
let finalChats = [];

// Only use local chats if we're not logged in or server loading failed
if (!user) {
  finalChats = loadedChats;
}

if (chatParam) {
  const chatExists = finalChats.some(chat => chat.id === chatParam);
  if (chatExists) {
    setCurrentChatId(chatParam);
    updateUrlParam(chatParam);
  }
}
    getFishyAIStatusUpdates(true);
  } catch (error) {
    console.error('Initialization error:', error);
  } finally {
    setIsAppInitialized(true);
    setIsLoading(false);
    setInterval(() => {
    if (IsResponding === false) {
      loadChatsFromSupabase();
    }
    }, 10000);

if (promptParam) {
  setTimeout(() => {
  setCurrentMessage(promptParam); // Assigns the chat ID after 150ms
  handleSubmit(null, promptParam); // Sends message 100ms after setting chat ID
}, 150);
}
  }// eslint-disable-next-line
}, [Wait_Seconds]); // Only include truly stable dependencies


const GetLBURL = useCallback(async () => {
  // Add a check to prevent multiple calls
  if (LBURL) return { SelectedServer: LBURL };

  try {
    const response = await fetch(`${LoadBalancer_URL}/data`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const data = await response.json();

    // Check if data is valid and has a Servers array
    let selectedServerUrl = null;

    if (data && data.Servers && data.Servers.length > 0) {
      // Filter only online servers
      const onlineServers = data.Servers.filter(s => s.status.toLowerCase() === "online");

      // If no online server, fallback to the first server
      if (onlineServers.length === 0) {
        selectedServerUrl = data.Servers[0].url;
      } else {
        // If there are online servers, find the fastest one
        const checkServerSpeed = async (url) => {
          const start = performance.now();
          try {
            const headResp = await fetch(url, { method: 'HEAD' });
            if (!headResp.ok) throw new Error(`HEAD request failed`);
            return performance.now() - start;
          } catch {
            return Infinity; // mark unreachable
          }
        };

        const serverTimes = await Promise.all(
          onlineServers.map(async (server) => ({
            url: server.url,
            time: await checkServerSpeed(server.url)
          }))
        );

        serverTimes.sort((a, b) => a.time - b.time);
        selectedServerUrl = serverTimes[0].url;
      }
    } else if (data && data.Domains && data.Domains.length > 0) {
      // If no servers or data.Servers is null, fall back to Domains
      const checkServerSpeed = async (url) => {
        const start = performance.now();
        try {
          const headResp = await fetch(url, { method: 'HEAD' });
          if (!headResp.ok) throw new Error(`HEAD request failed`);
          return performance.now() - start;
        } catch {
          return Infinity; // mark unreachable
        }
      };

      const domainTimes = await Promise.all(
        data.Domains.map(async (domain) => ({
          url: domain,
          time: await checkServerSpeed(domain)
        }))
      );

      domainTimes.sort((a, b) => a.time - b.time);
      selectedServerUrl = domainTimes[0].url;
    } else {
      // Handle the case where neither Servers nor Domains are available
      console.error("No valid server or domain data found.");
      return null;
    }

    if (selectedServerUrl) {
      const result = {
        Servers: data.Servers,
        Domains: data.Domains,
        SelectedServer: selectedServerUrl,
        SaveToSession: data.SaveToSession ?? true,
        SaveToStorage: data.SaveToStorage ?? false
      };

      setLBURL(selectedServerUrl);
      return result;
    }

  } catch (err) {
    console.error("Failed to fetch LoadBalancer data:", err);
    return null;
  }
}, [LBURL, LoadBalancer_URL]);

useEffect(() => {
  const init = async () => {
    if (!isAppInitialized && !hasInitialized.current) {
      await initializeApp();
      await loadSharedChat();
    }
  };
  init();// eslint-disable-next-line
}, [isAppInitialized, initializeApp]);

  useEffect(() => {
    if (isAppInitialized) {
      setTimeout(() => {
      window.location.reload();
      }, 1000);
    }// eslint-disable-next-line
  }, [allowEvents]);

  const handleManageAccount = async () => {
    window.open(`https://account.fishyai.crickdevs.com?token=${localStorage.getItem('token')}`, '_blank');
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await fetch(`${base_api_url}account/logout`, {
          headers: { 'Authorization': `Bearer ${token}` },
          method: 'POST'
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    localStorage.removeItem('token');
    setUser(null);
    setShowUserModal(false);
    setChats([]);
    window.location.reload();
  };

  useEffect(() => {
    localStorage.setItem('font_size', fontSize);
    document.documentElement.style.fontSize =
      fontSize === 'x-large' ? '20px' :
      fontSize === 'large' ? '18px' :
      fontSize === 'small' ? '12px' :
      '16px'; // default
  }, [fontSize]);

useEffect(() => {
  let themeToApply = theme;

  if (theme === "auto") {
    const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    themeToApply = systemPrefersDark ? "dark" : "light";
  }

  // If dark (or auto but resolves to dark) AND OLED mode is enabled
  if (isOledMode && themeToApply === "dark") {
    themeToApply = "oled";
  }

  document.documentElement.setAttribute("data-theme", themeToApply);
  localStorage.setItem("FishyAI_Theme", theme);
  if(document.getElementById('loading-screen-container')) {
    var lsc = document.getElementById('loading-screen-container')
    var lscs = lsc.style
    lscs.background = 'var(--chat-bg)'
    lscs.color = 'var(--text-primary)'
        //     <div
        //   className="loading-screen"
        //   id="loading-screen-container"
        //   style={{
        //     background: '#343541',
        //     zIndex: 99999999,
        //     position: 'fixed',
        //     inset: 0,
        //     display: 'flex',
        //     flexDirection: 'column',
        //     alignItems: 'center',
        //     justifyContent: 'center',
        //     opacity: !isLoading ? 0 : 1,
        //     transition: 'opacity 0.5s ease',
        //     pointerEvents: !isLoading ? 'none' : 'all'
        //   }}
        // >
  }
}, [theme, isOledMode]);

// eslint-disable-next-line
const MergeButton = () => {
  if (!showMergeButton || !currentChat?.isShared) return null;
  
  return (
    <div style={{
      position: 'fixed',
      top: '70px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 1000,
      background: 'var(--primary)',
      color: 'white',
      padding: '8px 16px',
      borderRadius: '6px',
      cursor: 'pointer',
      boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
      fontSize: '0.9rem',
      fontWeight: '600'
    }} onClick={mergeSharedChat}>
      <i className="fas fa-merge" style={{ marginRight: '8px' }}></i>
      Merge
    </div>
  );
};

  function getCurrentTheme(theme) {
  if (theme === "dark") return "dark";
  if (theme === "auto") {
    const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    return systemPrefersDark ? "dark" : "light";
  }
  return String(theme) ?? "light";
}

  function maskEmail(email) {
  if (!email) return "N/A"; // if undefined, null, or empty

  const [user, domain] = email.split("@");
  if (!domain) return email; // not a valid email, return as-is

  // show up to 3 characters, but at least 1
  const visiblePart = user.length > 3 ? user.slice(0, 3) : user.slice(0, 1);
  return `${visiblePart}***@${domain}`;
}

function formatDate(dateString, humanReadable = true) {
  const date = new Date(dateString);

  if (humanReadable) {
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();

    // Get ordinal suffix
    function getOrdinal(n) {
      if (n > 3 && n < 21) return "th";
      switch (n % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
      }
    }

    return `${day}${getOrdinal(day)} ${month} ${year}`;
  } else {
    return date.toLocaleDateString();
  }
}

  const handleModelChange = async (e) => {
    const provider = e.target.value;
    let newModel = { provider };
    
    if (provider === 'openrouter' && availableModels.Openrouter) {
      const firstAvailable = Object.keys(availableModels.Openrouter).find(
        model => availableModels.Openrouter[model]
      );
      newModel.openrouterModel = firstAvailable.toLowerCase().replace(/_/g, '-');
    }
    
    setSelectedModel(newModel);
    localStorage.setItem('selectedModel', JSON.stringify(newModel));
    
    let success
    if (localStorage.getItem('token')) {
      success = await saveModelToBackend(newModel);
    } else {
      success = true;
    }
    
    try {
      const response = await fetch(`${base_api_url}options?model=${selectedModel.provider}`);
      const data = await response.json();
      setFeatures({
        search: data.search === "true",
        attach: data.attach === "true",
        think: data.think === "true",
        message: true
      });

      if(!data.attach) {
        addToast({
          message: 'This model does not support attachments',
          type: 'info',
          duration: 5000
        });
      }
    } catch (err) {
      console.error("Failed to fetch features:", err);
      setFeatures({
        search: false,
        attach: false,
        think: false,
        message: true
      });
    } finally {
      window.location.reload();
    }
    
    if (success) {
      addToast({
        message: 'Model preference saved',
        type: 'success',
        duration: 3000
      });
    } else {
      addToast({
        message: 'Failed to save model preference',
        type: 'error',
        duration: 5000
      });
    }
  };

  function handleNoticeRead() {
    setShowTOSModal(false);
    localStorage.setItem('notice_read', 'true');
  }

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const res = await fetch(`${base_api_url}models`);
        if (res.ok) {
          const data = await res.json();
          setAvailableModels(data);
          
          if (!localStorage.getItem('selectedModel')) {
            if (data.Gemini) {
              setSelectedModel({ provider: 'gemini' });
            } else if (data.Mistral) {
              setSelectedModel({ provider: 'mistral' });
            } else if (data.Openrouter) {
              const firstAvailable = Object.keys(data.Openrouter).find(
                model => data.Openrouter[model]
              );
              if (firstAvailable) {
                setSelectedModel({
                  provider: 'openrouter',
                  openrouterModel: firstAvailable.toLowerCase().replace(/_/g, '-')
                });
              }
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch models:', err);
      }
    };

    const savedModel = localStorage.getItem('selectedModel');
    if (savedModel) {
      try {
        const parsed = JSON.parse(savedModel);
        if (parsed && typeof parsed === 'object' && parsed.provider) {
          setSelectedModel(parsed.provider);
        } else {
          setSelectedModel(savedModel);
        }
      } catch {
        setSelectedModel(savedModel);
      }
    }

    fetchModels();
  }, [base_api_url]);

  const saveModelToBackend = useCallback(async (newModel) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await fetch(`${base_api_url}account/model`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ model: newModel })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedModel(data.model.provider);
        localStorage.setItem('selectedModel', data.model.provider);
        return true;
      }
      throw new Error('Failed to save model');
    } catch (err) {
      console.error('Error saving model:', err);
      return false;
    }
  }, [base_api_url]);

  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (touchStartX === null) return;
    
    const touchX = e.touches[0].clientX;
    const diff = touchX - touchStartX;
    
    if (diff > 0) {
      const percentage = Math.min(100, (diff / window.innerWidth) * 100);
      setSwipePercentage(percentage);
    }
  };

  const handleTouchEnd = () => {
    if (swipePercentage > 25) {
      setSidebarOpen(true);
    } else {
      setSidebarOpen(false);
    }
    setTouchStartX(null);
    setSwipePercentage(0);
  };

const exportData = async () => {
  const token = localStorage.getItem('token');
  if (!token) return;
  if(!downloadPassword) {
    addToast({
      message: 'Please enter a password',
      type: 'error',
      duration: 3000
    });
    return;
  }
  try {
    const response = await fetch(`${base_api_url}account/data/download`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Password': downloadPassword
      }
    });

    if (!response.ok) {
      // Try to parse JSON error message if available
      let errorMessage = `Failed to download data from server. ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        };
      } catch {
        // ignore JSON parse errors here
      }
      throw new Error(errorMessage);
    }

    // Get the response as a Blob (binary data)
    const blob = await response.blob();

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);

    // Create a temporary <a> element to trigger download
    const a = document.createElement('a');
    a.href = url;

    // Try to get filename from content-disposition header or fallback to default
    const disposition = response.headers.get('Content-Disposition');
    let filename = `${user.username || 'user'}-data.zip`;
    if (disposition && disposition.includes('filename=')) {
      filename = disposition.split('filename=')[1].split(';')[0].replace(/"/g, '');
    }
    a.download = filename;

    document.body.appendChild(a);
    a.click();

    // Clean up
    a.remove();
    window.URL.revokeObjectURL(url);
    setShowDownloadModal(false);
  } catch (err) {
    console.error('Error downloading data from server: ' + err);
    addToast({
      message: err.message,
      type: 'error',
      duration: 3000
    });
  }
};

useCustomKeyHandlers({
  onSlashPress: () => document.getElementById('inputarea')?.focus(),
  onCtrlR: () => setIsEditingTitle(!isEditingTitle),
  onCtrlSpace: () => setSidebarOpen(!sidebarOpen),
  // onCtrlOnly: () => setSidebarOpen(!sidebarOpen),
  onCtrlK: () => setIsCommandPaletteOpen(prev => !prev)
});

  const importData = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const zip = new JSZip();
      const content = await zip.loadAsync(file);
      
      const dataFile = content.file("FishyAI-Userdata.json");
      if (!dataFile) throw new Error("Invalid export file");
      
      const data = JSON.parse(await dataFile.async("text"));
      
      setChats(data.chats || []);
      if (data.chats.length > 0) {
        setCurrentChatId(data.chats[0].id);
      }
      
      if (data.theme) setTheme(data.theme);
      
    } catch (err) {
      alert("Failed to import data: " + err.message);
    }
    e.target.value = null;
  };

  useEffect(() => {
    setLoading(true);
    fetch(`${base_api_url}options?model=${encodeURIComponent(selectedModel.provider)}`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(data => {
        setFeatures({
          attach: !!data.attach,
          search: !!data.search,
          think: !!data.think
        });
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
      });
  }, [selectedModel, base_api_url]);

  const selectChat = (chatId) => {
    if (activeStreamRef.current) {
      activeStreamRef.current.abort();
    }
    
    setChats(prev => {
      const updatedChats = prev.map(chat => 
        chat.id === chatId 
          ? { ...chat, lastUsed: new Date().toISOString() } 
          : chat
      );
      return updatedChats.sort((a, b) => new Date(b.lastUsed) - new Date(a.lastUsed));
    });
    
    setCurrentChatId(chatId);
    updateUrlParam(chatId);
    setSidebarOpen(false);
  };


  // CommandPalette.js

const CommandPalette = ({ 
  isOpen, 
  onClose, 
  user, 
  base_api_url, 
  setCurrentChatId, 
  setSidebarOpen,
  setIsSettingsOpen,
  setShowUserModal,
  handleLogout,
  createNewChat,
  setShowMathsModal,
  setShowHistoryModal,
  theme,
  isOledMode
}) => {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // keep track of abort controller & debounce
  const abortControllerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  const quickActions = [
    {
      id: 'new-chat',
      title: 'New Chat',
      description: 'Start a new conversation',
      icon: <FaPlus />,
      action: () => {
        createNewChat();
        onClose();
      }
    },
    {
      id: 'settings',
      title: 'Settings',
      description: 'Change app settings and preferences',
      icon: <FaCog />,
      action: () => {
        setIsSettingsOpen(true);
        onClose();
      }
    },
    {
      id: 'account',
      title: 'Account',
      description: 'View account information',
      icon: <FaUser />,
      action: () => {
        setShowUserModal(true);
        onClose();
      }
    },
    {
      id: 'maths-tool',
      title: 'Maths Calculator',
      description: 'Open the maths calculation tool',
      icon: <FaTools />,
      action: () => {
        setShowMathsModal(true);
        onClose();
      }
    },
    {
      id: 'calculation-history',
      title: 'Calculation History',
      description: 'View previous calculations',
      icon: <FaHistory />,
      action: () => {
        setShowHistoryModal(true);
        onClose();
      }
    },
    ...(user ? [{
      id: 'logout',
      title: 'Sign Out',
      description: 'Sign out of your account',
      icon: <FaSignOutAlt />,
      action: () => {
        handleLogout();
        onClose();
      }
    }] : [])
  ];

  const searchChats = useCallback(async (searchQuery) => {
    if (!user || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    // cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${base_api_url}account/chats/search?q=${encodeURIComponent(searchQuery)}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        }
      );

      if (response.ok) {
        const results = await response.json();
        setSearchResults(results);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [user, base_api_url]);

  // Debounce query typing
  useEffect(() => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      searchChats(query);
    }, 400); // wait 400ms after typing

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [query, searchChats]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      clearTimeout(debounceTimeoutRef.current);
    };
  }, []);

  // Compute results
  const filteredQuickActions = quickActions.filter(action =>
    action.title.toLowerCase().includes(query.toLowerCase()) ||
    action.description.toLowerCase().includes(query.toLowerCase())
  );

  const handleResultClick = (result) => {
    if (result.action) {
      result.action();
    } else if (result.id) {
      setCurrentChatId(result.id);
      setSidebarOpen(false);
      onClose();
    }
  };

  const allResults = [
    ...filteredQuickActions,
    ...searchResults.map(result => ({
      ...result,
      icon: <FaComments />,
      action: () => handleResultClick(result)
    }))
  ];

  // Auto focus when open
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setSelectedIndex(0);
    }
  }, [isOpen, query]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1));
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        return;
      }

      if (e.key === 'Enter' && allResults.length > 0) {
        e.preventDefault();
        const selectedResult = allResults[selectedIndex];
        if (selectedResult) {
          handleResultClick(selectedResult);
        }
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);// eslint-disable-next-line
  }, [isOpen, selectedIndex, allResults, onClose]);

  return (
    <>
      {isOpen && (
        <div className="command-palette-overlay" onClick={onClose}>
          <div 
            className={`command-palette ${isOledMode ? 'oled-mode' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="command-palette-header">
              <input
                ref={inputRef}
                type="text"
                placeholder="Type a command or search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="command-input"
              />
            </div>
            
            <div className="command-results" ref={resultsRef}>
              {loading && (
                <div className="command-item">
                  <div className="command-loader">Searching...</div>
                </div>
              )}
              
              {!loading && allResults.length === 0 && query && (
                <div className="command-item">
                  <div className="command-no-results">No results found</div>
                </div>
              )}
              
              {allResults.map((result, index) => (
                <div
                  key={result.id || index}
                  className={`command-item ${index === selectedIndex ? 'selected' : ''}`}
                  onClick={() => handleResultClick(result)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="command-icon">{result.icon}</div>
                  <div className="command-content">
                    <div className="command-title">{result.title}</div>
                    <div className="command-description">{result.description}</div>
                    {result.keywords && (
                      <div className="command-keywords">
                        {result.keywords.slice(0, 3).map((keyword, i) => (
                          <span key={i} className="keyword-tag">#{keyword}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// CommandPalette.js

  return (
    <div id="app" className="app-container">
      {showLoader && (
        <div
          className="loading-screen"
          id="loading-screen-container"
          style={{
            background: '#343541',
            zIndex: 99999999,
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: !isLoading ? 0 : 1,
            transition: 'opacity 0.5s ease',
            pointerEvents: !isLoading ? 'none' : 'all'
          }}
        >
          <img className="main-logo" id="spinner-icon" src="/fishyai.png" alt="FishyAI Icon" />
          <p style={{ fontWeight: 'bold' }}>Loading FishyAI</p>
        </div>
      )}

      <div 
        className={`sidebar ${sidebarOpen ? 'open' : ''}`}
        style={{
          transform: touchStartX !== null 
            ? `translateX(${-100 + swipePercentage}%)` 
            : sidebarOpen ? 'translateX(0)' : 'translateX(-100%)'
        }}
      >
        <div className="sidebar-header">
          <div onClick={(e) => {  setSidebarOpen(false); window.location.href = 'https://crickdevs.com' }} className="ht-home" role="img" aria-label="HT Logo"></div>
          <button className="new-chat-btn" onClick={createNewChat}>
            <i className="fas fa-plus"></i> New Chat
          </button>
        </div>
        
        <div className="chat-history">
{chats.map(chat => (
  <div 
    key={chat.id}
    className={`chat-item ${currentChatId === chat.id ? 'active' : ''} ${chat.isShared ? 'shared-chat' : ''}`}
    onClick={() => selectChat(chat.id)}
  >
    <i className="fas fa-comment chat-icon"></i>
    <div className="chat-info">
      <span className={`chat-title ${chat.isShared ? 'shared-title' : ''}`} title={chat.title}>{chat.isShared ? (<li className="fa fa-link"></li>) : null}
        {chat.title}
        {chat.isShared && !chat.sharedFrom && (
          <span className="anonymous-chat-indicator">(anonymous)</span>
        )}
      </span>
      <div className="chat-actions">
        {user && !chat.isShared && (
          <button 
            className="action-btn"
            onClick={(e) => {
              e.stopPropagation();
              setIsShareModalOpen(true);
              setChatToShare(chat);
            }}
          >
            <i className="fas fa-share-alt"></i>
          </button>
        )}
        <button 
          className="action-btn"
          onClick={(e) => {
            e.stopPropagation();
            setChatToDelete(chat.id);
            setIsDeleteModalOpen(true);
          }}
        >
          <i className="fas fa-trash-alt"></i>
        </button>
      </div>
    </div>
  </div>
))}
        </div>
        
        <div className="sidebar-footer">
          <div 
  className="user-settings-container"
  onClick={(e) => {
    e.stopPropagation();
    setIsCommandPaletteOpen(true);
    setSidebarOpen(false)
  }}
>
  <div className="user-action">
    <div className="user-avatar"><i className="fas fa-magnifying-glass"></i></div>
    <div className="action-text">
      <span className="action-title">Command Palette</span>
      <span className="action-shortcut">CTRL + K</span>
    </div>
  </div>
</div>

          <div 
            className="user-settings-container"
onClick={() => {

  if(user) {
    if (Acknowledges?.requirements?.acknowledges?.IntroducingTools === false) {
      SetShowIntroducingCard(true, 'IntroducingTools'); // lowercase s
    } else {
      setToolsVisible(true);
    }
  } else {
    setShowLoginModal(true);
  }
}}
          >
            <div className="user-info">
              <div className="user-avatar"><i className="fas fa-tools"></i></div>
              <span className="sidebar-text">Tools</span>
            </div>
          </div>

          <div 
            className="user-settings-container"
            onClick={(e) => {
              e.stopPropagation();
              setIsSettingsOpen(true);
              setSidebarOpen(false)
            }}
          >
            <div className="user-info">
              <div className="user-avatar"><i className="fas fa-cog"></i></div>
              <span className="sidebar-text">Settings</span>
            </div>
          </div>

          <div 
            className="user-settings-container"
            onClick={() => user ? setShowUserModal(true) : setShowLoginModal(true)}
          >
            <div className="user-info">
              <div className="user-avatar">
                {user ? (
                  user.username.charAt(0).toUpperCase()
                ) : (
                  <i className="fas fa-user"></i>
                )}
              </div>
              <span className="username-span">
                {/* {user ? user.username : 'Guest'} */ 'Account'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div 
        className="swipe-indicator" 
        style={{ transform: `scaleY(${swipePercentage / 100})` }}
      />

      {sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div 
        className={`main-content ${touchStartX !== null ? 'swiping' : ''}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="chat-header">
          <button 
            className="menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <i className={sidebarOpen ? "fas fa-times" : "fas fa-bars"}></i>
          </button>
          
<div className="chat-title-container">
  {isEditingTitle ? (
    <input
      type="text"
      className="chat-title-input"
      value={currentChat?.title || ""}
      onChange={(e) => {
        setChats(prev => prev.map(chat => 
          chat.id === currentChatId 
            ? { ...chat, title: e.target.value } 
            : chat
        ));
      }}
      onBlur={() => setIsEditingTitle(false)}
      onKeyDown={(e) => {
        if (e.key === "Enter") setIsEditingTitle(false);
      }}
      autoFocus
    />
  ) : (
    <>
      <span>
        {currentChat?.title || "FishyAI"}
        {currentChat?.isShared && (
          <span className="shared-chat-badge">
            <i className="fas fa-share"></i>
            {currentChat.sharedFrom ? `from ${currentChat.sharedFrom}` : 'shared'}
          </span>
        )}
      </span>
      {currentChat && !currentChat.isShared && (
        <button 
          className="action-btn"
          onClick={() => setIsEditingTitle(true)}
        >
          <i className="fas fa-edit"></i>
        </button>
      )}
    </>
  )}
</div>
        </div>

        {/* Add this right after the chat-header closing div */}
{showMergeButton && currentChat?.isShared && (
  <div 
    className={`merge-button ${isMerging ? 'loading' : ''}`}
    onClick={mergeSharedChat}
    style={{
      position: 'fixed',
      top: '70px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 1000,
      background: 'var(--primary)',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '8px',
      cursor: isMerging ? 'not-allowed' : 'pointer',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      fontSize: '0.9rem',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease'
    }}
  >
    <i className={`fas ${isMerging ? 'fa-spinner fa-spin' : 'fa-code-merge'}`}></i>
    {isMerging ? 'Merging...' : 'Merge to Account'}
  </div>
)}
        
        <div 
          className="chat-messages" 
          ref={chatMessagesRef}
        >
          {messages.length === 0 ? (
            !showAdvancedEmptyState ? (
              <div id="advanced-empty-state" className="empty-state">
                <img className="main-logo" src={iconSrc} style={iconStyling} alt="FishyAI Icon" />
                <h3>FishyAI <span className="beta">BETA</span></h3>
                <div className="examples">
                  <p></p>
                  <ul>
                    {randomExamples.map((example, index) => (
                      <li
                        key={index}
                        onClick={() => {
                          setCurrentMessage(example);
                          setTimeout(() => {
                            document.querySelector(".input-form")?.dispatchEvent(
                              new Event("submit", { bubbles: true, cancelable: true })
                            );
                          }, 0);
                        }}
                      >
                        {example}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div id="empty-state" className="empty-state">
                <img className="main-logo" src={iconSrc} style={iconStyling} alt="FishyAI Icon" />
                <h1 id="welcome-message" style={welcomeStyling} className="welcome-message">Welcome to FishyAI</h1>
                <div className="examples">
                  <p></p>
                  <ul>
                    {randomExamples.map((example, index) => (
                      <li
                        key={index}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = isOledMode ? "var(--text-primary)" : getRandomColor();
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = "var(--text-secondary)";
                        }}
                        onClick={() => {
                          setCurrentMessage(example);
                          setTimeout(() => {
                            document.querySelector(".input-form")?.dispatchEvent(
                              new Event("submit", { bubbles: true, cancelable: true })
                            );
                          }, 0);
                        }}
                      >
                        {example}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )
          ) : (
            <>
              {searchStatus && (
                <div className="message ai-msg status-message">
                  <div className="avatar">
                    <i className="fas fa-robot"></i>
                  </div>
                  <div className="message-content">
                    <div className="message-text">{searchStatus}</div>
                  </div>
                </div>
              )}
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`message ${message.role === "user" ? "user-msg" : "ai-msg"}`}
                >
                  <div className="avatar">
                    {message.role === "user" ? (
                      <i className="fas fa-user"></i>
                    ) : (
                      <i className="fas fa-robot"></i>
                    )}
                  </div>
                  <div className="message-content">
                    {message.files && message.files.length > 0 && (
                      <div className="attached-files">
                        <strong>Attached files:</strong>
                        <ul>
                          {message.files.map((file, index) => (
                            <li key={index}>
                              <i className="fas fa-paperclip"></i> {file.name}
                              <span className="file-size">({Math.round(file.size / 1024)}KB)</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="message-text">
                      {message.role === "assistant" ? (
                        <MarkdownRenderer content={message.content} />
                      ) : (
                        <div>{message.content}</div>
                      )}
                    </div>

                    <div className="message-actions">
                      <button
                        className="action-btn"
                        onClick={() => copyToClipboard(message.content)}
                      >
                        <i className="far fa-copy"></i> Copy
                      </button>
                      {message.role === "assistant" && messages[messages.length - 1].id === message.id && (
                        <button className="action-btn" onClick={regenerateResponse}>
                          <i className="fas fa-redo"></i> Regenerate
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        <div 
          className={`input-container ${isInputFocused ? 'fixed-for-mobile' : ''}`}
          ref={inputContainerRef}
        >
          {attachedFiles.length > 0 && (
            <div className="file-attachments">
              <strong>Attached files:</strong>
              <div className="file-list">
                {attachedFiles.map((file, index) => (
                  <div key={index} className="file-item">
                    <i className="fas fa-paperclip"></i> 
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">({Math.round(file.size / 1024)}KB)</span>
                    <button 
                      className="remove-file"
                      onClick={() => removeFile(index)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="input-area">
            {features.attach && (
              <button 
                className="attach-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={attachedFiles.length >= 3 || loading}
                title="Attach file (max 10MB)"
              >
                <i className="fas fa-paperclip"></i>
              </button>
            )}
            
            {features.search && (
              <button 
                className={`search-btn ${searchMode ? 'active' : ''}`}
                onClick={() => {
                  if (!user) {
                    addToast({
                      message: 'You must be logged in to use search',
                      type: 'error',
                      onClick: () => {
                        setShowLoginModal(true);
                      },
                      duration: 3000
                    });
                    return;
                  }
                  setSearchMode(!searchMode);
                }}
                title={searchMode ? "Disable search" : "Enable search"}
                disabled={loading}
              >
                <i className="fas fa-magnifying-glass"></i>
              </button>
            )}
            
            {features.think === true && (
              <button 
                className={`search-btn ${thinkMode ? 'active' : ''}`}
                onClick={() => {
                  if (!user) {
                    addToast({
                      message: 'You must be logged in to use thinking',
                      type: 'error',
                      onClick: () => {
                        setShowLoginModal(true);
                      },
                      duration: 3000
                    });
                    return;
                  }
                  setThinkMode(!thinkMode);
                }}
                title={thinkMode ? "Disable thinking" : "Enable thinking"}
                disabled={loading}
              >
                <i className="fas fa-lightbulb"></i>
              </button>
            )}
            
            <form onSubmit={handleSubmit} className="input-form">
              <textarea
                ref={textareaRef}
                required
                rows={1}
                id="inputarea"
                placeholder="Message FishyAI..."
                value={currentMessage}
                className="input"
                onChange={(e) => {
                  setCurrentMessage(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                }}
                disabled={loading}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                onFocus={() => {
                  setIsInputFocused(true);
                  setTimeout(() => {
                    if (textareaRef.current) {
                      textareaRef.current.scrollIntoView({ 
                        behavior: "smooth", 
                        block: "nearest" 
                      });
                    }
                  }, 100);
                }}
                onBlur={() => setIsInputFocused(false)}
              />
              
              {loading ? (
                <button
                  type="button"
                  className="submit-btn cancel-btn"
                  onClick={cancelGeneration}
                >
                  <i className="fas fa-times"></i>
                </button>
              ) : (
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={!currentMessage.trim() && attachedFiles.length === 0}
                >
                  <i className="fas fa-arrow-up"></i>
                </button>
              )}
            </form>
          </div>
          <div className="input-footer">
            FishyAI can make mistakes. Verify important information.
          </div>
        </div>
      </div>

      {isSettingsOpen && (
        <div className="modal-backdrop" onClick={() => setIsSettingsOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Settings</h3>
              <button className="close-btn" onClick={() => setIsSettingsOpen(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body"> 
              {availableModels && (
                <div className="setting-group" style={{ display: 'none' }}>
                  <h4>Model Provider</h4>
                  <div className="option">
                    <select
                      value={selectedModel}
                      onChange={handleModelChange}
                    >
                      {availableModels.Gemini && <option value="gemini">Gemini</option>}
                      {availableModels.Mistral && <option value="mistral">Mistral</option>}
                      {availableModels.Openrouter === true && <option value="openrouter">OpenRouter</option>}
                    </select>
                  </div>
                </div>
              )}

              <div className="setting-group">
                <h4>Personalization</h4>
                  <div className="option">
                    <label className="debug-checkbox">
                      <div className="checkbox-container">
                        <input
                          type="checkbox"
                          checked={allowEvents}
                          onChange={(e) => {
                            setAllowEvents(e.target.checked);
                            localStorage.setItem('allowEvents', e.target.checked);
                          }}
                        />
                        <span className="checkmark"></span>
                      </div>
                      <span className="debug-label">Allow events</span>
                    </label>
                  </div>
                <h5>Font Size</h5>
                <select value={fontSize} onChange={e => setFontSize(e.target.value)}>
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                  <option value="x-large">X-Large</option>
                </select>
                
                <h5>Theme</h5>
                <div className="theme-options">
                  <div 
                    className={`theme-option ${theme === 'auto' ? 'active' : ''}`}
                    onClick={() => setTheme('auto')}
                  >
                    <div className="theme-preview auto-theme">
                      <div className="auto-dark-half"></div>
                      <div className="auto-light-half"></div>
                    </div>
                    <span>Auto</span>
                  </div>
                  <div 
                    className={`theme-option ${theme === 'dark' ? 'active' : ''}`}
                    onClick={() => setTheme('dark')}
                  >
                    <div className="theme-preview dark-theme"></div>
                    <span>Dark</span>
                  </div>
                  <div 
                    className={`theme-option ${theme === 'light' ? 'active' : ''}`}
                    onClick={() => setTheme('light')}
                  >
                    <div className="theme-preview light-theme"></div>
                    <span>Light</span>
                  </div>
                  <div 
                    className={`theme-option ${theme === 'fishy' ? 'active' : ''}`}
                    onClick={() => setTheme('fishy')}
                  >
                    <div className="theme-preview fishy-theme"></div>
                    <span>Fishy</span>
                  </div>
                  <div 
                    className={`theme-option ${theme === 'solar' ? 'active' : ''}`}
                    onClick={() => setTheme('solar')}
                  >
                    <div className="theme-preview solar-theme"></div>
                    <span>Solar</span>
                  </div>
                </div>
                {getCurrentTheme(theme) === 'dark' && (
                  <div className="option">
                    <label className="debug-checkbox">
                      <div className="checkbox-container">
                        <input
                          type="checkbox"
                          checked={isOledMode}
                          onChange={(e) => {
                            setIsOledMode(e.target.checked);
                            localStorage.setItem('oledmode', e.target.checked);
                          }}
                        />
                        <span className="checkmark"></span>
                      </div>
                      <span className="debug-label">Enable Pure Black (OLED)</span>
                    </label>
                  </div>
                )}
              </div>
              
              {is_dev_mode && (
                <div id="dev_mode" className="setting-group">
                  <h4>Developer Mode</h4>
                  <div className="option">
                    <label className="debug-checkbox">
                      <div className="checkbox-container">
                        <input
                          type="checkbox"
                          checked={isDebugMode}
                          onChange={(e) => {
                            setIsDebugMode(e.target.checked);
                            localStorage.setItem('debugmode', e.target.checked);
                          }}
                        />
                        <span className="checkmark"></span>
                      </div>
                      <span className="debug-label">Use dev server (Port: 4033)</span>
                    </label>
                  </div>
                  <div className="option">
                    <button 
                      className="btn btn-secondary" 
                      onClick={(e) => {
                        disable_devmode();
                        setTimeout(() => {
                          window.location.reload();
                        }, 1000);
                      }}
                    >
                      <i className="fas fa-eye-slash"></i> Disable Developer Mode
                    </button>
                  </div>
                </div>
              )}
            
              <div className="setting-group">
                <h4>Data Management</h4>
                <div className="option">
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => setIsClearModalOpen(true)}
                  >
                    <i className="fas fa-trash"></i> Clear Chat History
                  </button>
                </div>
                <div className="option">
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => setIsClearAllModalOpen(true)}
                  >
                    <i className="fas fa-trash"></i> Clear All Data
                  </button>
                </div>
                {user && (
                <div className="option">
                  <button className="btn btn-secondary" onClick={() => {setIsSettingsOpen(false); setShowDownloadModal(true)}}>
                    <i className="fas fa-download"></i> Download Data
                  </button>
                  
                  <input
                    type="file"
                    id="import-data"
                    accept=".zip"
                    onChange={importData}
                    style={{ display: 'none' }}
                  />
                </div>
                )}
              </div>

              <div className="setting-group2 altdiv">
                <button style={{ marginBottom: '8px' }} className="btn btn-secondary" onClick={() => {window.open('https://fishyai.crickdevs.com/TOS', '_blank')}}>Terms of Service</button>
                <button style={{ marginBottom: '8px' }} className="btn btn-secondary" onClick={() => {window.open('https://fishyai.crickdevs.com/PP', '_blank')}}>Privacy Policy</button>
                <h4 style={{ marginTop: '20px' }}>About FishyAI</h4>
                <p>FishyAI is an AI chat application for common questions.</p>
                {builds && is_dev_mode === true && (
                  <p>Build: {builds.APP_BUILD_DATE_PRETTY ?? builds.APP_BUILD_DATE ?? 'N/A'}</p>
                )}
                <p>Version: {builds.APP_VERSION_PRETTY ?? builds.APP_VERSION ?? 'N/A'}</p>
                <p>AI powered by <span className="link" onClick={() => window.open('https://gemini.google.com', '_blank')}>Google Gemini</span></p>
                <p>Developed by <span className="link" onClick={() => window.open('https://crickdevs.com', '_blank')}>CrickDevs</span></p>
                <div onClick={() => click_version()} className="ht-logo" role="img" aria-label="HT Logo"></div>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn btn-primary"
                onClick={() => setIsSettingsOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isClearModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsClearModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Clear All Chats</h3>
              <button className="close-btn" onClick={() => setIsClearModalOpen(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <p>Are you sure you want to delete ALL chat history? This cannot be undone.</p>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setIsClearModalOpen(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={async () => {
                  await clearAllChats();
                  setIsClearModalOpen(false);
                }}
              >
                <i className="fas fa-trash"></i> Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {isClearAllModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsClearAllModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Clear All Data</h3>
              <button className="close-btn" onClick={() => setIsClearAllModalOpen(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <p>Are you sure you want to clear ALL data? This does not affect your account data if you are logged in. But it will clear all of your local chat history.</p>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setIsClearAllModalOpen(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={async () => {
                  await clearAllData();
                  setIsClearAllModalOpen(false);
                }}
              >
                <i className="fas fa-trash"></i> Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsDeleteModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Chat</h3>
              <button className="close-btn" onClick={() => setIsDeleteModalOpen(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <p>Are you sure you want to delete this chat? This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={() => deleteChat(chatToDelete)}
              >
                <i className="fas fa-trash"></i> Delete
              </button>
            </div>
          </div>
        </div>
      )}

{IsShareModalOpen && (
  <div className="modal-backdrop" onClick={() => setIsShareModalOpen(false)}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h3>Share Chat</h3>
        <button className="close-btn" onClick={() => setIsShareModalOpen(false)}>
          <i className="fas fa-times"></i>
        </button>
      </div>

<label>
  Uses
  <input
    type="number"
    value={ChatToShare.uses}
    min={1}
    max={100}
    onChange={(e) =>
      setChatToShare({ ...ChatToShare, uses: Number(e.target.value) })
    }
  />
</label>

<div className="option">
  <label className="debug-checkbox">
    <div className="checkbox-container">
      <input
        type="checkbox"
        checked={ChatToShare.anonymous ?? false}
        onChange={(e) =>
          setChatToShare({ ...ChatToShare, anonymous: e.target.checked })
        }
      />
      <span className="checkmark"></span>
    </div>
    <span className="debug-label">Share Anonymously</span>
  </label>
</div>

      <p>By sharing this chat, you acknowledge that anyone with the link can access it. <br /> This action cannot be undone.</p>

      <div className="modal-actions">
        <button
          className="btn btn-secondary"
          onClick={() => setIsShareModalOpen(false)}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={() =>
            shareChat(ChatToShare.id, ChatToShare.uses, ChatToShare.anonymous)
          }
        >
          <i className="fas fa-share"></i> Share
        </button>
      </div>
    </div>
  </div>
)}

{IsChatSharedModalOpen && (
  <div className="modal-backdrop" onClick={() => setIsChatSharedModalOpen(false)}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h3>Chat shared</h3>
        <button className="close-btn" onClick={() => setIsChatSharedModalOpen(false)}>
          <i className="fas fa-times"></i>
        </button>
      </div>

<h3
  onClick={() => {
    copyToClipboard(SharedChatURL);
    addToast({
      autoClose: true,
      message: 'Link copied to clipboard',
      type: 'info',
      duration: 2500,
    });
  }}
  style={{
    cursor: 'pointer',
    whiteSpace: 'nowrap',       // Prevent wrapping
    overflow: 'hidden',         // Hide overflow
    textOverflow: 'ellipsis',   // Show "..." if too long
    display: 'inline-block',    // Fit width to content
    maxWidth: '100%',           // Don't exceed container
  }}
>
  {SharedChatURL}
</h3>
<p>This link will expire in 1 month. And can be used {ChatToShare.uses ?? 5} times</p>

      <div className="modal-actions">
        <button
          className="btn btn-secondary"
          onClick={() => setIsChatSharedModalOpen(false)}
        >
          Close
        </button>
        <button
          className="btn btn-primary"
onClick={() => {
    copyToClipboard(SharedChatURL);
    addToast({
        autoClose: true,
        message: `Link copied to clipboard`,
        type: 'info',
        duration: 2500
    });
}}
        >
          <i className="fas fa-clipboard"></i> Copy link
        </button>
      </div>
    </div>
  </div>
)}
      
      {showLoginModal && (
        <div className="modal-backdrop" onClick={() => setShowLoginModal(false)}>
          <div className="modal-content login-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Login to FishyAI</h3>
              <button className="close-btn" onClick={() => setShowLoginModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="login-modal-body">
              <div className="login-logo">
                <FaUserCircle size={50} color="#ff9f1c" className="bounce" />
              </div>
              {authError && <div className="auth-error">{authError}</div>}
              <p className="login-subtitle">Access your account to sync conversations across devices</p>
              <button 
                className="btn btn-primary login-btn"
                onClick={() => {
                  const authUrl = isDebugMode 
                    ? `http://localhost:9999/index.html?debug=${isDebugMode}&theme=${theme}` 
                    : `https://auth.fishyai.crickdevs.com?theme=${theme}&debug=${isDebugMode}&redirect=${encodeURIComponent(window.location.href)}`;
                  window.location.href = authUrl;
                }}
                disabled={isVerifyingToken}
              >
                {isVerifyingToken ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <>
                    <i className="fas fa-sign-in-alt"></i> Login / Sign Up
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showURLAlertModal && (
        <div className="modal-backdrop" onClick={() => setShowURLAlertModal(false)}>
          <div className="modal-content login-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Warning</h3>
              <button className="close-btn" onClick={() => setShowURLAlertModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="login-modal-body">
              <div className="login-logo">
                <FaLink size={50} color="#ff9f1c" className="bounce" />
              </div>
              {authError && <div className="auth-error">{authError}</div>}
              <p className="login-subtitle">This link may be unsafe. Proceed at your own risk.</p>
              <p className="description">{AlertedURL}</p>
              <button 
                className="btn btn-primary login-btn"
                onClick={() => {
                  setShowURLAlertModal(false);
                  const hrefurl = AlertedURL;
                  window.open(hrefurl, '_blank');
                }}
                disabled={isVerifyingToken}
              >
                {isVerifyingToken ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <>
                    <i className="fas fa-sign-in-alt"></i> Continue
                  </>
                )}
              </button>
              <button 
                className="btn btn-secondary login-btn"
                onClick={() => setShowURLAlertModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showtosmodal && localStorage.getItem('notice_read') !== 'true' && (
        <div className="modal-backdrop">
          <div className="modal-content tos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Notice</h3>
            </div>
            <div className="login-modal-body">
              <div className="login-logo">
                <FaBook size={50} color="#ff9f1c" className="bounce"/>
              </div>
              {authError && <div className="auth-error">{authError}</div>}
              <p>By continuing, you agree to our <span className="link" onClick={() => window.open('https://fishyai.crickdevs.com/TOS', '_blank')}>Terms of Service</span> and <span className="link" onClick={() => window.open('https://fishyai.crickdevs.com/PP', '_blank')}>Privacy Policy</span></p>
              <button 
                className="btn btn-primary login-btn"
                onClick={() => {
                  handleNoticeRead();
                }}
                disabled={isVerifyingToken}
              >
                {isVerifyingToken ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <>
                    <i className="fas fa-thumbs-up"></i> Continue
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showdevmodal && (
        <div className="modal-backdrop">
          <div className="modal-content tos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Notice</h3>
            </div>
            <div className="login-modal-body">
              <div className="login-logo">
                <FaBook size={50} color="#ff9f1c" className="bounce"/>
              </div>
              <p>Are you sure you want to enable developer mode? There is no guarantee of stability and may cause issues.</p>
              <button 
                className="btn btn-primary login-btn"
                onClick={() => {
                  enable_devmode();
                  setShowDevModal(false);
                }}
                disabled={isVerifyingToken}
              >
                {isVerifyingToken ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <>
                    <i className="fas fa-thumbs-up"></i> Continue
                  </>
                )}
              </button>
              <button 
                className="btn btn-primary login-btn"
                onClick={() => {
                  disable_devmode();
                  setShowDevModal(false);
                }}
                disabled={isVerifyingToken}
              >
                {isVerifyingToken ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <>
                    <i className="fas fa-thumbs-down"></i> Cancel
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showdownloadmodal && (
        <div className="modal-backdrop" onClick={() => setShowDownloadModal(false)}>
          <div className="modal-content tos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Notice</h3>
              <button className="close-btn" onClick={() => setShowDownloadModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="login-modal-body">
              <div className="login-logo">
                <FaFile size={50} color="#ff9f1c" className="bounce"/>
              </div>
              {authError && <div className="auth-error">{authError}</div>}
              <p className="login-subtitle">By downloading your data, you agree that we hold no responsibility if it is lost, leaked, or accessed by others after the download.</p>
              <input
                type="password"
                placeholder="Password"
                onChange={(e) => setDownloadPassword(e.target.value)}
              />
              <button 
                className="btn btn-primary login-btn"
                onClick={() => {
                  exportData();
                }}
                disabled={isVerifyingToken}
              >
                {isVerifyingToken ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <>
                    <i className="fas fa-save"></i> Download
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showUserModal && (
        <div className="modal-backdrop" onClick={() => setShowUserModal(false)}>
          <div className="modal-content user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Account Information</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setShowUserModal(false);
                  setSidebarOpen(false);
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="user-modal-body">
              <div className="user-avatar large">
                {user?.username?.charAt(0).toUpperCase() || <i className="fas fa-user"></i>}
              </div>
              <div className="user-details">
                <div className="user-detail">
                  <span className="detail-label">Username:</span>
                  <span className="detail-value">{user?.username || 'User'}</span>
                </div>
                <div className="user-detail">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{maskEmail(user?.email)}</span>
                </div>
                <div className="user-detail">
                  <span className="detail-label">Country:</span>
                  <span className="detail-value">{user?.cc || 'Unknown'}</span>
                </div>
                <div className="user-detail">
                  <span className="detail-label">Registered:</span>
                  <span className="detail-value">
                  {user?.created_at ? formatDate(user.created_at) : 'N/A'}
                  </span>
                </div>
                {user && (
                  <button
                    className="btn btn-secondary"
                    style={{
                      width: '100%',
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    onClick={handleManageAccount}
                  >
                    Manage Account<i className="fas fa-arrow-up-right-from-square"></i>
                  </button>
                )}
              </div>
              <div className="storage-info">
                <i className="fas fa-database" style={{ marginRight: '5px' }}></i>
                {user ? "Chats synced to cloud" : "Chats stored locally"}
              </div>
              <div className="modal-actions">
                <button className="btn btn-danger" onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt"></i> Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

{IntroducingToolsCard && (
  <div className="modal-backdrop introducing-tools">
    <div className="modal-content introducing-tools-modal">
      <div className="tools-modal-header">
        <div className="tools-icon-container">
          <FaTools size={32} />
        </div>
        <h3>Introducing Powerful New Tools</h3>
      </div>
      <div className="tools-modal-body">
        <p>Supercharge your productivity with our new toolkit designed to enhance your workflow and streamline your tasks.</p>
        
        <div className="tools-features">
          <div className="tool-feature">
            <div className="tool-feature-icon">
              <FaBolt size={24} />
            </div>
            <div className="tool-feature-content">
              <h4>Enhanced Efficiency</h4>
              <p>Complete tasks faster with specialized tools that automate complex processes.</p>
            </div>
          </div>
          
          <div className="tool-feature">
            <div className="tool-feature-icon">
              <FaSearch size={24} />
            </div>
            <div className="tool-feature-content">
              <h4>Deeper Insights</h4>
              <p>Gain valuable insights with analytical tools that help you understand your data better.</p>
            </div>
          </div>
          
          <div className="tool-feature">
            <div className="tool-feature-icon">
              <FaBullseye size={24} />
            </div>
            <div className="tool-feature-content">
              <h4>Precision Tools</h4>
              <p>Execute tasks with accuracy using specialized instruments designed for specific needs.</p>
            </div>
          </div>
        </div>
      </div>
      <div className="tools-modal-footer">
        <button 
          className="btn btn-primary"
          onClick={() => {
            SetShowIntroducingCard(false, 'IntroducingTools');
            Acknowledge(true, 'IntroducingTools', UserToken);
            setToolsVisible(true);
          }}
        >
          Explore Tools
        </button>
      </div>
    </div>
  </div>
)}


{ToolsVisible && (
        <div className="modal-backdrop">
          <div className="modal-content tools-modal">

          {/* Header */}
          <div className="modal-header">
            <h3>Available Tools</h3>
          </div>

          {/* Body */}
          <div className="modal-body">
<div className="tools-grid">
  {Tools && Tools.length > 0 ? (
    Tools.map((tool) => (
      <div onClick={() => {
        setShowMathsModal(true);
        setToolsVisible(false)
      }} key={tool.name} style={{ display: tool.available ? 'block' : 'none' }} className="tool-card">
        <div className="tool-icon"><img src={tool.icon} className="No-Highlight" alt={tool.name} width={50}/></div>
        <h4 className="tool-name No-Highlight">{tool.name}</h4>
        <p className="tool-description No-Highlight">{tool.description}</p>
      </div>
    ))
  ) : (
    <p>Loading tools...</p>
  )}
</div>

          </div>

          {/* Footer / Actions */}
          <div className="modal-actions">
            <button className="btn btn-primary" onClick={() => setToolsVisible(false)}>
              Close
            </button>
          </div>

        </div>
      </div>
)}
{showMathsModal && (
  <div className="modal-backdrop">
    <div className="modal-content maths-modal" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h3>Maths Calculator<span className="description">Powered by <span>PiMind</span></span></h3>
        <button className="close-btn" onClick={() => setShowMathsModal(false)}>
          <i className="fas fa-times"></i>
        </button>
      </div>
      
      <div className="modal-body">
        <div className="maths-input-section">
          <div className="input-group">
            <label>Calculation Expression</label>
            <input
              type="text"
              value={calculationInput}
              onChange={(e) => setCalculationInput(e.target.value)}
              placeholder="e.g., x+y-z or sum_{n=1}^{10} n"
              disabled={isCalculating}
            />
          </div>
          
          <div className="variables-section">
            <label>Variables</label>
            {variables.map((variable, index) => (
              <div key={index} className="variable-input">
                <input
  type="text"
  placeholder="Variable name"
  value={variable.name}
  onChange={(e) => {
    const onlyLetters = e.target.value.replace(/[^a-zA-Z]/g, ''); // remove anything that's not a letter
    updateVariable(index, 'name', onlyLetters);
  }}
  disabled={isCalculating}
/>

                <span>=</span>
                <input
                  type="number"
                  placeholder="Value"
                  value={variable.value}
                  onChange={(e) => updateVariable(index, 'value', e.target.value)}
                  disabled={isCalculating}
                />
                <button 
                  onClick={() => removeVariable(index)}
                  disabled={variables.length <= 1 || isCalculating}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
            <button 
              onClick={addVariable}
              disabled={isCalculating}
              className="add-variable-btn"
            >
              <i className="fas fa-plus"></i> Add Variable
            </button>
          </div>
          
          <button 
            onClick={calculateMaths}
            disabled={!calculationInput || isCalculating}
            className="calculate-btn"
          >
            {isCalculating ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Calculating...
              </>
            ) : (
              <>
                <i className="fas fa-calculator"></i> Calculate
              </>
            )}
          </button>
          {UserToken && (
  <button 
    className="btn btn-secondary"
    onClick={fetchCalculationHistory}
    style={{ marginLeft: '10px' }}
  >
    <i className="fas fa-history"></i> View History
  </button>
)}
        </div>
        
        <div style={{ display: 'none' }} id="console" className="maths-console">
          <h4>Calculation Log</h4>
          <div className="console-output">
            {calculationProgress.map((item, index) => (
              <div key={index} className={`console-line ${item.type}`}>
                {item.type === 'progress' && (
                  <><i className="fas fa-cog fa-spin"></i> {item.data}</>
                )}
                {item.type === 'error' && (
                  <><i className="fas fa-exclamation-triangle"></i> {item.data}</>
                )}
                {item.type === 'result' && (
                  <><i className="fas fa-check-circle"></i> Calculation complete</>
                )}
              </div>
            ))}
            {calculationProgress.length === 0 && (
              <div className="console-placeholder">
                Calculation log will appear here...
              </div>
            )}
          </div>
        </div>
        
        {finalResult && (
          <div className="maths-result">
            <h4>Result</h4>
            <div className="result-summary">
              <div className="result-expression">{finalResult.expression}</div>
              <div className="result-error">{finalResult.error}</div>
              <div className="result-value">= {finalResult.value}</div>
              <div className="result-time">Time: {finalResult.timeTaken}</div>
            </div>
            
{finalResult.explanation && finalResult.explanation.length > 0 && (
  <div className="result-explanation">
    <details>
      <summary>Show Calculation Steps</summary>
      <div className="steps-container">
        {finalResult.explanation.map((step, index) => (
          <div key={index} className="step">
            {step}
          </div>
        ))}
      </div>
    </details>
  </div>
)}
            
            <div className="result-actions">
              <button style={{ display: AllowDownloadingCalculations ? 'block' : 'none' }} onClick={downloadCalculation} className="download-btn">
                <i className="fas fa-download"></i> Download Result
              </button>
              <button onClick={() => {
                setCalculationInput("");
                setVariables([{ name: "", value: "" }]);
                setCalculationProgress([]);
                setFinalResult(null);
              }} className="clear-btn">
                <i className="fas fa-trash"></i> Clear
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
)}

{showHistoryModal && (
  <div className="modal-backdrop" onClick={() => setShowHistoryModal(false)}>
    <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h3>Calculation History</h3>
        <button className="close-btn" onClick={() => setShowHistoryModal(false)}>
          <i className="fas fa-times"></i>
        </button>
      </div>
      
      <div className="modal-body">
        {calculationHistory.length === 0 ? (
          <p>No calculation history found</p>
        ) : (
          <div className="history-list">
            {calculationHistory.map((calc) => (
              <div key={calc.id} className="history-item" onClick={() => viewCalculation(calc)}>
                <div className="history-expression">{calc.expression}</div>
                <div className="history-details">
                  <span className="history-result">{calc.value}</span>
                  <span className="history-time">{calc.timeTaken}</span>
                  {calc.AdvancedThinked && <span className="history-advanced">Advanced</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
)}

      {availableModels && 
        !availableModels.Gemini && 
        !availableModels.Mistral && 
        (!availableModels.Openrouter || 
          Object.values(availableModels.Openrouter).every(v => !v)) && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <div className="modal-header">
              <h3>No Models Available</h3>
            </div>
            <p>Sorry, no AI models are currently available. Please try again later.</p>
            <div className="modal-actions">
              <button 
                className="btn btn-primary"
                onClick={() => window.location.reload()}
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}
      
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileUpload}
        multiple
        accept=".png, .jpg, .jpeg, .pdf, .docx"
      />
<CommandPalette
  isOpen={isCommandPaletteOpen}
  onClose={() => setIsCommandPaletteOpen(false)}
  user={user}
  base_api_url={base_api_url}
  setCurrentChatId={setCurrentChatId}
  setSidebarOpen={setSidebarOpen}
  setIsSettingsOpen={setIsSettingsOpen}
  setShowUserModal={setShowUserModal}
  handleLogout={handleLogout}
  createNewChat={createNewChat}
  setShowMathsModal={setShowMathsModal}
  setShowHistoryModal={setShowHistoryModal}
  theme={theme}
  isOledMode={isOledMode}
/>
    </div>
  );
}

export default AppWrapper;